import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import { assetUrl } from '@/utils/asset-url';
import DSModal from "@/componentsDS/Modal/Modal.jsx";
import DSCardConfigure from "@/componentsDS/Modal/CardConfigure.jsx";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import JsonViewer from "./components/JsonViewer";
import JsonUpload from "./components/JsonUpload";
import JsonDownload from "./components/JsonDownload";
import XAllOfBuilder, {
	emitRule,
	extractRulesFromSchema,
	XAllOfRulesView,
	buildFieldToRulesIndex,
} from "./components/xallof";
import FieldDetailsPanel from "./components/view/FieldDetailsPanel";
import CustomValidatorsPanel from "./components/customvalidators";
import RenderHintsForm from "./components/render-hints";
import {
	applyCustomValidatorsToSchema,
	extractCustomValidators,
	sanitizeCustomValidators,
} from "./custom-validators";
import ValidationPreview from "./components/ValidationPreview";
import VersionComparison from "./components/compare/VersionComparison";
import {
	SAMPLE_VALUES,
	addChildToField,
	buildJsonSchemaFromRules,
	buildPayloadTemplate,
	buildQuerySchemaFromElementSchema,
	diffAgainstBase,
	diffCustomValidators,
	diffFieldOrder,
	diffRootMetadata,
	diffXAllOfRules,
	formatDataType,
	formatExampleValueForInput,
	getFieldConstraintErrors,
	getFieldDepth,
	getSchemaConstraintErrors,
	parseExampleValue,
	moveFieldInTree,
	removeFieldFromTree,
	reorderRequired,
	sortFieldsAttachmentsLast,
	updateFieldInTree,
	validateSchemaShape,
} from "./dsv-schema-utils";
import { summarize as summarizeRule } from "./components/xallof/summarize";
import { computeNextVersion } from "./compute-next-version";
import { getEnvAndDex } from "@/utils/get-env-and-dex";
import useDataSchemaVersioning from "@/hooks/use-data-schema-versioning/index";
import { extractSchemaFields, pickHydrationSchema } from "@/hooks/use-data-schema-versioning/index";
import { useBuildex } from "@/hooks/use-buildex";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page";
import useToast from "@/hooks/use-toast";
import Toast from "@/components/Toast";
import AffectedOrganisations from "./components/AffectedOrganisations";
import { useNavigate, useParams } from "@/lib/router-dom";
import rem from "@/utils/rem.js";
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

// ============ Constants matching BE ============

const CHANGE_TYPES = [
	{ name: 'Patch (Bug fixes)', value: 'PATCH' },
	{ name: 'Minor (New features)', value: 'MINOR' },
	{ name: 'Major (Breaking changes)', value: 'MAJOR' },
];

const DATA_TYPES = [
	{ name: 'string', value: 'string' },
	{ name: 'integer', value: 'integer' },
	{ name: 'number', value: 'number' },
	{ name: 'boolean', value: 'boolean' },
	{ name: 'date', value: 'date' },
	{ name: 'date-time', value: 'date-time' },
	{ name: 'array', value: 'array' },
	{ name: 'object', value: 'object' },
];

const MAX_NESTING_DEPTH = 6;

// Root-level metadata classifications. Annotation-only — not consumed by AJV
// validation. Preserved on round-trip via applyRootClassifications overlay.
const SECURITY_CLASSIFICATIONS = [
	{ name: 'None', value: 'None' },
	{ name: 'Official (Open)', value: 'Official (Open)' },
	{ name: 'Official (Closed)', value: 'Official (Closed)' },
	{ name: 'Restricted', value: 'Restricted' },
	{ name: 'Confidential', value: 'Confidential' },
	{ name: 'Confidential (Cloud-Eligible)', value: 'Confidential (Cloud-Eligible)' },
	{ name: 'Secret', value: 'Secret' },
	{ name: 'Top Secret', value: 'Top Secret' },
];

const SENSITIVITY_CLASSIFICATIONS = [
	{ name: 'None', value: 'None' },
	{ name: 'Non-Sensitive', value: 'Non-Sensitive' },
	{ name: 'Sensitive Normal', value: 'Sensitive Normal' },
	{ name: 'Sensitive High', value: 'Sensitive High' },
];

// Single classification dropdown — used twice (security + sensitivity).
// When isEditable, renders a <select> with a disabled "Select <label>…"
// placeholder + the supplied options; otherwise a read-only value chip.
function ClassificationField({ name, label, options, value, onChange, error, isEditable }) {
	const selectId = `dsv-${name}-classification-select`;
	const valueId = `dsv-${name}-classification-value`;
	return (
		<div className="dsv-update__classifications-field">
			<label className="dsv-update__version-label" htmlFor={selectId}>{label}</label>
			{isEditable ? (
				<>
					<select
						id={selectId}
						className="dsv-modal__select"
						data-testid={selectId}
						value={value}
						onChange={onChange}
					>
						<option value="" disabled>{`Select ${label}…`}</option>
						{options.map((c) => (
							<option key={c.value} value={c.value}>{c.name}</option>
						))}
					</select>
					{error && <span className="dsv-form-error">{error}</span>}
				</>
			) : (
				<div className="dsv-update__classifications-value" data-testid={valueId}>
					{value || '(not set)'}
				</div>
			)}
		</div>
	);
}

ClassificationField.propTypes = {
	name: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(PropTypes.shape({
		name: PropTypes.string.isRequired,
		value: PropTypes.string.isRequired,
	})).isRequired,
	value: PropTypes.string,
	onChange: PropTypes.func,
	error: PropTypes.string,
	isEditable: PropTypes.bool,
};

// ============ Transition Config ============

const TRANSITION_CONFIG = {
	DRAFT: {
		targetStatus: 'ACTIVE',
		buttonLabel: 'Promote to Active',
		buttonClass: 'dsv-btn--promote',
		modalTitle: 'Promote Version',
		verb: 'promoted to Active',
		showExpiryDate: () => true,
		expiryRequired: (version) => version.changeType === 'MAJOR',
		expiryHint: 'Required for MAJOR versions (min 14 days from today). Optional for Minor/Patch — auto-inherits from parent MAJOR.',
		defaultExpiryDays: 14,
		confirmationMessage: null,
		getDisabledReason: () => null,
		getEnabledHint: () => null,
	},
	ACTIVE: {
		targetStatus: 'DEPRECATED',
		buttonLabel: 'Deprecate',
		buttonClass: 'dsv-btn--deprecate',
		modalTitle: 'Deprecate Version',
		verb: 'deprecated',
		showExpiryDate: () => true,
		expiryRequired: () => false,
		expiryHint: 'Optionally set an expiry date for the deprecation period. Defaults to 30 days.',
		defaultExpiryDays: 30,
		confirmationMessage: 'This version will be marked as deprecated. Consumers should migrate to a newer version.',
		getDisabledReason: () => null,
		getEnabledHint: () => null,
	},
	DEPRECATED: {
		targetStatus: 'RETIRED',
		buttonLabel: 'Retire',
		buttonClass: 'dsv-btn--retire',
		modalTitle: 'Retire Version',
		verb: 'retired',
		showExpiryDate: () => false,
		expiryRequired: () => false,
		expiryHint: null,
		confirmationMessage: 'This version will be permanently retired and cannot be undone. For MAJOR versions, this may fail if the expiry date has not yet passed.',
		getDisabledReason: (version) => {
			if (version.changeType === 'MAJOR' && version.expiry && new Date(version.expiry) > new Date()) {
				return `Cannot retire MAJOR version before expiry date (${formatDate(version.expiry)})`;
			}
			return null;
		},
		getEnabledHint: (version) => {
			if (version.changeType !== 'MAJOR' && version.expiry && new Date(version.expiry) > new Date()) {
				return `${version.changeType} versions can be retired before expiry`;
			}
			return null;
		},
	},
};

function getTransitionConfig(status) {
	return TRANSITION_CONFIG[status] || null;
}

function formatDate(dateStr) {
	if (!dateStr) return '-';
	return new Date(dateStr).toISOString().split('T')[0];
}

function getDefaultExpiryDate(days = 14) {
	const date = new Date();
	date.setDate(date.getDate() + days);
	// Format from LOCAL calendar parts, not toISOString() (which is UTC). In a
	// UTC+ timezone (the admin is SG-deployed, UTC+8) the early-morning hours
	// fall on the previous UTC day, so toISOString() would yield days-1 and
	// weaken the MAJOR 14-day minimum the picker enforces.
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

// ============ Status Badges ============

function StatusBadge({ status }) {
	const statusMap = {
		'ACTIVE': { className: 'dsv-status--active', label: 'Active' },
		'DRAFT': { className: 'dsv-status--draft', label: 'Draft' },
		'DEPRECATED': { className: 'dsv-status--migrating', label: 'Deprecated' },
		'RETIRED': { className: 'dsv-status--retired', label: 'Retired' },
	};
	const config = statusMap[status] || statusMap['DRAFT'];
	return <span className={`dsv-status ${config.className}`}>{config.label}</span>;
}

StatusBadge.propTypes = { status: PropTypes.string };

function FieldStatusBadge({ status }) {
	if (!status) return null;
	const statusMap = {
		'Modified': { className: 'dsv-field-status--modified', label: 'Modified' },
		'New': { className: 'dsv-field-status--new', label: 'New' },
		'Deleted': { className: 'dsv-field-status--deleted', label: 'Deleted' },
	};
	const config = statusMap[status];
	if (!config) return null;
	return <span className={`dsv-field-status ${config.className}`}>{config.label}</span>;
}

FieldStatusBadge.propTypes = { status: PropTypes.string };

function ChangeTypeBadge({ changeType }) {
	const typeMap = {
		'PATCH': { className: 'dsv-change-type--patch', label: 'Patch' },
		'MINOR': { className: 'dsv-change-type--minor', label: 'Minor' },
		'MAJOR': { className: 'dsv-change-type--major', label: 'Major' },
		'INITIAL': { className: 'dsv-change-type--initial', label: 'Initial' },
	};
	const config = typeMap[changeType] || typeMap['INITIAL'];
	return <span className={`dsv-change-type ${config.className}`}>{config.label}</span>;
}

ChangeTypeBadge.propTypes = { changeType: PropTypes.string };

// ============ Promote Modal ============

function PromoteModal({ visible, hide, version, onConfirm }) {
	const config = version ? getTransitionConfig(version.status) : null;
	const defaultDays = config?.defaultExpiryDays ?? 14;
	const [expiryDate, setExpiryDate] = useState(() => getDefaultExpiryDate(defaultDays));
	const [error, setError] = useState('');

	useEffect(() => {
		if (visible) {
			setExpiryDate(getDefaultExpiryDate(defaultDays));
			setError('');
		}
	}, [visible, defaultDays]);

	if (!version) return null;
	if (!config) return null;

	const shouldShowExpiry = config.showExpiryDate(version);
	const isExpiryRequired = config.expiryRequired(version);

	const minDate = (version.status === 'DRAFT' && version.changeType === 'MAJOR')
		? getDefaultExpiryDate(14)
		: new Date().toISOString().split('T')[0];

	function handleConfirm() {
		const expiry = shouldShowExpiry ? (expiryDate || null) : null;
		if (shouldShowExpiry && expiry && expiry < minDate) {
			setError(`Please double-check the expiry date — it can't be earlier than ${minDate}.`);
			return;
		}
		onConfirm(config.targetStatus, expiry);
	}

	return (
		<DSModal visible={visible} close={hide} variant="small" dismissClickOutsideModal={false}>
			<DSCardConfigure style={{ minWidth: 600 }}>
				<DSCardConfigure.Header>
					<DSCardConfigure.Header.Title>{config.modalTitle}</DSCardConfigure.Header.Title>
					<DSCardConfigure.Header.Close>
						<button className="dsv-icon-btn" onClick={hide} title="Close">&times;</button>
					</DSCardConfigure.Header.Close>
				</DSCardConfigure.Header>
				<DSCardConfigure.Body>
					<DSCardConfigure.Body.Col>
						<p className="dsv-confirm__subtitle">
							Promote <strong>v{version.version}</strong> from{' '}
							<StatusBadge status={version.status} /> to{' '}
							<StatusBadge status={config.targetStatus} />
						</p>

						<div className="dsv-confirm__section">
							<h3 className="dsv-confirm__section-title">Version Details</h3>
							<div className="dsv-confirm__details">
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Version</span>
									<span className="dsv-confirm__value">v{version.version}</span>
								</div>
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Change Type</span>
									<span className="dsv-confirm__value"><ChangeTypeBadge changeType={version.changeType} /></span>
								</div>
								{version.changeDescription && (
									<div className="dsv-confirm__row">
										<span className="dsv-confirm__label">Description</span>
										<span className="dsv-confirm__value">{version.changeDescription}</span>
									</div>
								)}
							</div>
						</div>

						{config.confirmationMessage && (
							<div className="dsv-confirm__section">
								<p className="dsv-promote__hint" style={{ color: '#b45309' }}>
									{config.confirmationMessage}
								</p>
							</div>
						)}

						{shouldShowExpiry && (
							<div className="dsv-confirm__section">
								<h3 className="dsv-confirm__section-title">
									Expiry Date{isExpiryRequired ? '' : ' (Optional)'}
								</h3>
								<p className="dsv-promote__hint">
									{config.expiryHint}
								</p>
								<input
									className="dsv-modal__input"
									data-testid="promote-expiry-input"
									type="date"
									value={expiryDate}
									min={minDate}
									onChange={(e) => { setExpiryDate(e.target.value); setError(''); }}
								/>
								{error && (
									<p className="dsv-promote__hint" data-testid="promote-expiry-error" style={{ color: '#b45309' }}>
										{error}
									</p>
								)}
							</div>
						)}
					</DSCardConfigure.Body.Col>
				</DSCardConfigure.Body>
				<DSCardConfigure.Footer>
					<DSCardConfigure.Footer.Left />
					<DSCardConfigure.Footer.Right>
						<button className="dsv-btn dsv-btn--ghost" onClick={hide}>Cancel</button>
						<button className={`dsv-btn ${config.buttonClass}`} style={{ marginLeft: rem(12) }} onClick={handleConfirm}>
							{config.buttonLabel}
						</button>
					</DSCardConfigure.Footer.Right>
				</DSCardConfigure.Footer>
			</DSCardConfigure>
		</DSModal>
	);
}

PromoteModal.propTypes = {
	visible: PropTypes.bool,
	hide: PropTypes.func,
	version: PropTypes.object,
	onConfirm: PropTypes.func,
};

// ============ Schema Fields Table ============

// Tenant-aware hover tint for the view-mode clickable rows. We resolve the
// dex once at module load — getEnvAndDex throws when neither hostname nor
// the REACT_APP_DEX_ENV env var can identify the tenant (e.g. in unit
// tests), so swallow that and fall back to a neutral hover.
function safeResolveDex() {
	try {
		return getEnvAndDex().dex;
	} catch (_e) {
		return null;
	}
}

const VIEW_ROW_HOVER_BY_DEX = {
	sgbuildex: '#dbeafe', // blue-100
	sgtradex: '#ede9fe',  // violet-100
	sghealthdex: '#dcfce7', // green-100 (parity for completeness)
};

const DEFAULT_VIEW_ROW_HOVER = '#f3f4f6'; // grey

function getViewRowHoverColor() {
	const dex = safeResolveDex();
	return VIEW_ROW_HOVER_BY_DEX[dex] || DEFAULT_VIEW_ROW_HOVER;
}

function SchemaFieldRow({ field, depth, onEditField, onDeleteField, onAddChild, onMoveUp, onMoveDown, canMoveUp, canMoveDown, canEditSchema, viewMode, fieldToRulesIndex }) {
	const hasChildren = field.children && field.children.length > 0;
	const [expanded, setExpanded] = useState(hasChildren);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const canAddChild = canEditSchema && field.status !== 'Deleted' && (
		field.dataType === 'object' ||
		(field.dataType === 'array' && field.arrayItemType === 'object')
	);
	const fieldId = field.fieldId || field.dataElementName;
	const ruleIndices = viewMode && fieldToRulesIndex
		? fieldToRulesIndex.get(field.dataElementName) || []
		: [];

	// Auto-expand when children are added
	useEffect(() => {
		if (hasChildren) setExpanded(true);
	}, [field.children?.length]);

	const showRowExpander = viewMode && !canEditSchema;
	const detailsChevron = detailsOpen ? '▾' : '▸';

	const toggleDetails = () => setDetailsOpen((v) => !v);
	const handleRowKeyDown = (e) => {
		if (!showRowExpander) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			toggleDetails();
		}
	};
	const [rowHovered, setRowHovered] = useState(false);
	const viewRowHoverColor = showRowExpander ? getViewRowHoverColor() : null;
	const rowStyle = showRowExpander
		? {
			cursor: 'pointer',
			backgroundColor: detailsOpen || rowHovered ? viewRowHoverColor : undefined,
			transition: 'background-color 120ms ease',
		}
		: undefined;

	return (
		<>
			<div
				className={`dsv-schema__table-row ${field.status ? `dsv-schema__table-row--${field.status.toLowerCase()}` : ''}${showRowExpander ? ' dsv-schema__table-row--clickable' : ''}`}
				role={showRowExpander ? 'button' : undefined}
				tabIndex={showRowExpander ? 0 : undefined}
				aria-expanded={showRowExpander ? detailsOpen : undefined}
				aria-label={showRowExpander ? (detailsOpen ? `Hide details for ${field.dataElementName}` : `Show details for ${field.dataElementName}`) : undefined}
				data-testid={showRowExpander ? `dsv-view-field-row-expand-${fieldId}` : undefined}
				onClick={showRowExpander ? toggleDetails : undefined}
				onKeyDown={showRowExpander ? handleRowKeyDown : undefined}
				onMouseEnter={showRowExpander ? () => setRowHovered(true) : undefined}
				onMouseLeave={showRowExpander ? () => setRowHovered(false) : undefined}
				style={rowStyle}
			>
				{canEditSchema && (
					<div className="dsv-schema__table-cell dsv-schema__col--reorder" onClick={(e) => e.stopPropagation()}>
						{canMoveUp && field.status !== 'Deleted' && (
							<button
								type="button"
								className="dsv-icon-btn"
								onClick={() => onMoveUp && onMoveUp(field)}
								title="Move up"
								aria-label={`Move ${field.dataElementName} up`}
								data-testid={`dsv-field-move-up-${field.fieldId}`}
							>
								<svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
									<path d="M1 5L5 1L9 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</button>
						)}
						{canMoveDown && field.status !== 'Deleted' && (
							<button
								type="button"
								className="dsv-icon-btn"
								onClick={() => onMoveDown && onMoveDown(field)}
								title="Move down"
								aria-label={`Move ${field.dataElementName} down`}
								data-testid={`dsv-field-move-down-${field.fieldId}`}
							>
								<svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
									<path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</button>
						)}
					</div>
				)}
				<div className="dsv-schema__table-cell dsv-schema__col--name" title={field.dataElementName}>
					<div style={{ paddingLeft: depth * 20 + (hasChildren ? 0 : 20) }}>
						{hasChildren && (
							<button
								type="button"
								className="dsv-schema__toggle"
								style={{ fontSize: 16, cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
								onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev); }}
								aria-label={expanded ? 'Collapse' : 'Expand'}
							>
								{expanded ? '▾' : '▸'}
							</button>
						)}
						{showRowExpander && (
							<span
								className="dsv-view__row-chevron"
								aria-hidden="true"
								style={{ fontSize: 14, marginRight: 4, color: '#6b7280' }}
							>
								{detailsChevron}
							</span>
						)}
						{field.dataElementName}
						<FieldStatusBadge status={field.status} />
						{showRowExpander && ruleIndices.length > 0 && (
							<span
								data-testid={`dsv-view-field-row-conditional-${fieldId}`}
								style={{
									display: 'inline-block',
									marginLeft: 8,
									padding: '0 6px',
									background: '#fef3c7',
									color: '#92400e',
									borderRadius: 3,
									fontSize: 10,
									fontWeight: 600,
									textTransform: 'uppercase',
									letterSpacing: '0.02em',
								}}
								title={`Referenced in Rule ${ruleIndices.map((n) => n + 1).join(', Rule ')}`}
							>
								Conditional
							</span>
						)}
					</div>
				</div>
				<div className="dsv-schema__table-cell dsv-schema__col--business" title={field.businessTerm}>
					{field.businessTerm}
				</div>
				<div className="dsv-schema__table-cell dsv-schema__col--mandatory">
					<span className={`dsv-mandatory-badge ${field.mandatory ? 'dsv-mandatory-badge--mandatory' : 'dsv-mandatory-badge--optional'}`}>
						{field.mandatory ? 'Mandatory' : 'Optional'}
					</span>
				</div>
				<div className="dsv-schema__table-cell dsv-schema__col--datatype">
					{field.dataType === 'array' && field.arrayItemType
						? `array<${field.arrayItemType}>`
						: field.dataType}
				</div>
				{canEditSchema && (
					<div className="dsv-schema__table-cell dsv-schema__col--field-actions">
						{canAddChild && (
							<button className="dsv-icon-btn" onClick={() => onAddChild(field)} title="Add child field">
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<path d="M8 3.333V12.667" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									<path d="M3.333 8H12.667" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</button>
						)}
						<button className="dsv-icon-btn" onClick={() => onEditField(field)} title="Edit field">
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path d="M11.333 2C11.508 1.825 11.716 1.686 11.945 1.591C12.174 1.497 12.419 1.448 12.666 1.448C12.914 1.448 13.159 1.497 13.388 1.591C13.617 1.686 13.825 1.825 14 2C14.175 2.175 14.314 2.383 14.408 2.612C14.503 2.841 14.552 3.086 14.552 3.333C14.552 3.581 14.503 3.826 14.408 4.055C14.314 4.284 14.175 4.492 14 4.667L5 13.667L1.333 14.667L2.333 11L11.333 2Z" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
						<button className="dsv-icon-btn dsv-icon-btn--delete" onClick={() => onDeleteField(field)} title="Delete field">
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path d="M2 4H14" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
								<path d="M5.333 4V2.667C5.333 2.313 5.473 1.974 5.724 1.724C5.974 1.474 6.313 1.333 6.666 1.333H9.333C9.687 1.333 10.026 1.474 10.276 1.724C10.526 1.974 10.666 2.313 10.666 2.667V4M12.666 4V13.333C12.666 13.687 12.526 14.026 12.276 14.276C12.026 14.526 11.687 14.667 11.333 14.667H4.666C4.313 14.667 3.974 14.526 3.724 14.276C3.473 14.026 3.333 13.687 3.333 13.333V4H12.666Z" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</div>
				)}
			</div>
			{showRowExpander && detailsOpen && (
				<div className="dsv-schema__table-row dsv-schema__table-row--details" style={{ background: '#fafbfc' }}>
					<div className="dsv-schema__table-cell" style={{ gridColumn: '1 / -1' }}>
						<div style={{ paddingLeft: depth * 20 + 20 }}>
							<FieldDetailsPanel
								field={field}
								conditionalRuleIndices={ruleIndices}
							/>
						</div>
					</div>
				</div>
			)}
			{expanded && hasChildren && field.children.map((child, childIdx) => (
				<SchemaFieldRow
					key={child.fieldId}
					field={child}
					depth={depth + 1}
					onEditField={onEditField}
					onDeleteField={onDeleteField}
					onAddChild={onAddChild}
					onMoveUp={onMoveUp}
					onMoveDown={onMoveDown}
					canMoveUp={childIdx > 0}
					canMoveDown={childIdx < field.children.length - 1}
					canEditSchema={canEditSchema}
					viewMode={viewMode}
					fieldToRulesIndex={fieldToRulesIndex}
				/>
			))}
		</>
	);
}

SchemaFieldRow.propTypes = {
	field: PropTypes.object,
	depth: PropTypes.number,
	onEditField: PropTypes.func,
	onDeleteField: PropTypes.func,
	onAddChild: PropTypes.func,
	onMoveUp: PropTypes.func,
	onMoveDown: PropTypes.func,
	canMoveUp: PropTypes.bool,
	canMoveDown: PropTypes.bool,
	canEditSchema: PropTypes.bool,
	viewMode: PropTypes.bool,
	fieldToRulesIndex: PropTypes.instanceOf(Map),
};

function SchemaFieldsTable({ fields, onEditField, onDeleteField, onAddChild, onMoveUp, onMoveDown, canEditSchema, viewMode, fieldToRulesIndex }) {
	const displayFields = sortFieldsAttachmentsLast(fields);
	let lastNonAttachmentIdx = -1;
	displayFields.forEach((f, i) => {
		if ((f.dataElementName || '').toLowerCase() !== 'attachments') lastNonAttachmentIdx = i;
	});

	return (
		<div className="dsv-schema__fields">
			<div className="dsv-schema__fields-header">
				<h3 className="dsv-schema__fields-title">
					Schema Fields ({fields.filter(f => f.status !== 'Deleted').length})
				</h3>
				<p className="dsv-schema__fields-subtitle">
					{canEditSchema
						? 'Manage data element fields and their properties'
						: 'Click a field to view its data dictionary, validation rules, and renderer hints.'}
				</p>
			</div>

			<div className="dsv-schema__table">
				<div className="dsv-schema__table-head">
					{canEditSchema && (
						<div className="dsv-schema__table-cell dsv-schema__col--reorder" aria-label="Reorder" />
					)}
					<div className="dsv-schema__table-cell dsv-schema__col--name">Technical Field Name</div>
					<div className="dsv-schema__table-cell dsv-schema__col--business">Business Term</div>
					<div className="dsv-schema__table-cell dsv-schema__col--mandatory">Mandatory/Optional</div>
					<div className="dsv-schema__table-cell dsv-schema__col--datatype">Data Type</div>
					{canEditSchema && (
						<div className="dsv-schema__table-cell dsv-schema__col--field-actions">Actions</div>
					)}
				</div>

				{displayFields.length > 0 ? (
					displayFields.map((field, idx) => {
						const isAttachments = (field.dataElementName || '').toLowerCase() === 'attachments';
						return (
							<SchemaFieldRow
								key={field.fieldId}
								field={field}
								depth={0}
								onEditField={onEditField}
								onDeleteField={onDeleteField}
								onAddChild={onAddChild}
								onMoveUp={onMoveUp}
								onMoveDown={onMoveDown}
								canMoveUp={!isAttachments && idx > 0}
								canMoveDown={!isAttachments && idx < lastNonAttachmentIdx}
								canEditSchema={canEditSchema}
								viewMode={viewMode}
								fieldToRulesIndex={fieldToRulesIndex}
							/>
						);
					})
				) : (
					<div className="dsv-schema__table-empty">No schema fields defined yet.</div>
				)}
			</div>
		</div>
	);
}

SchemaFieldsTable.propTypes = {
	fields: PropTypes.array,
	onEditField: PropTypes.func,
	onDeleteField: PropTypes.func,
	onAddChild: PropTypes.func,
	onMoveUp: PropTypes.func,
	onMoveDown: PropTypes.func,
	canEditSchema: PropTypes.bool,
	viewMode: PropTypes.bool,
	fieldToRulesIndex: PropTypes.instanceOf(Map),
};

// ============ Add/Edit Field Modal ============

function toSnakeCaseTechnicalFieldName(value) {
	return String(value || '')
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
		.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
		.replace(/['’]/g, '')
		.replace(/[^A-Za-z0-9]+/g, '_')
		.replace(/_+/g, '_')
		.replace(/^_+|_+$/g, '')
		.toLowerCase();
}

export function FieldModal({ visible, hide, field, onSave, parentFieldId, currentDepth }) {
	const [formData, setFormData] = useState({
		dataElementName: '', businessTerm: '', mandatory: true, dataType: 'string', arrayItemType: 'object', exampleValue: '',
		description: '', businessDefinition: '', applicableStandardName: '', dataFormat: '',
		xUitype: '', xUioptions: undefined,
		minLength: '', maxLength: '', minimum: '', maximum: '', exclusiveMinimum: '', exclusiveMaximum: '',
		pattern: '', enumValues: '', format: '', multipleOf: '', minItems: '', maxItems: '', uniqueItems: false,
	});
	const [error, setError] = useState(null);

	useEffect(() => {
		if (field) {
			setFormData({
				dataElementName: field.dataElementName || '',
				businessTerm: field.businessTerm || '',
				mandatory: field.mandatory !== undefined ? field.mandatory : true,
				dataType: field.dataType || 'string',
				arrayItemType: field.arrayItemType || 'object',
				exampleValue: formatExampleValueForInput(field.exampleValue),
				description: field.description ?? '',
				businessDefinition: field.businessDefinition ?? '',
				applicableStandardName: field.applicableStandardName ?? '',
				dataFormat: field.dataFormat ?? '',
				xUitype: field.xUitype || '',
				xUioptions: field.xUioptions,
				minLength: field.minLength ?? '',
				maxLength: field.maxLength ?? '',
				minimum: field.minimum ?? '',
				maximum: field.maximum ?? '',
				exclusiveMinimum: field.exclusiveMinimum ?? '',
				exclusiveMaximum: field.exclusiveMaximum ?? '',
				pattern: field.pattern ?? '',
				enumValues: field.enumValues ? (Array.isArray(field.enumValues) ? field.enumValues.join(', ') : field.enumValues) : '',
				format: field.format ?? '',
				multipleOf: field.multipleOf ?? '',
				minItems: field.minItems ?? '',
				maxItems: field.maxItems ?? '',
				uniqueItems: field.uniqueItems ?? false,
			});
		} else {
			setFormData({ dataElementName: '', businessTerm: '', mandatory: true, dataType: 'string', arrayItemType: 'object', exampleValue: '',
				description: '', businessDefinition: '', applicableStandardName: '', dataFormat: '',
				xUitype: '', xUioptions: undefined,
				minLength: '', maxLength: '', minimum: '', maximum: '', exclusiveMinimum: '', exclusiveMaximum: '',
				pattern: '', enumValues: '', format: '', multipleOf: '', minItems: '', maxItems: '', uniqueItems: false,
			});
		}
		setError(null);
	}, [field, visible]);

	// Exactly three ways to close this modal: the X button, ESC, or a
	// successful submit. Backdrop-dismiss and the Cancel button are removed.
	useEffect(() => {
		if (!visible) return undefined;
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') hide();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [visible, hide]);

	const handleChange = (key, value) => { setFormData(prev => ({ ...prev, [key]: value })); setError(null); };
	const handleBusinessTermChange = (value) => {
		setFormData((prev) => {
			if (field?.fieldId) {
				return { ...prev, businessTerm: value };
			}
			const previousAutoKey = toSnakeCaseTechnicalFieldName(prev.businessTerm);
			const shouldAutoFillKey = !prev.dataElementName || prev.dataElementName === previousAutoKey;
			const shouldAutoFillBusinessDefinition = prev.businessDefinition === prev.businessTerm;
			const shouldDefaultApplicableStandardName = prev.applicableStandardName === '' && prev.businessTerm === '';
			return {
				...prev,
				businessTerm: value,
				dataElementName: shouldAutoFillKey ? toSnakeCaseTechnicalFieldName(value) : prev.dataElementName,
				businessDefinition: shouldAutoFillBusinessDefinition ? value : prev.businessDefinition,
				applicableStandardName: shouldDefaultApplicableStandardName && value.trim()
					? 'NA'
					: prev.applicableStandardName,
			};
		});
		setError(null);
	};

	const handleSubmit = () => {
		// x-uioptions arrives from RenderHintsForm already in the right
		// shape per uitype: a JSON-encoded string for dropdown /
		// dynamic-select, a native array for radio / multi-select, a
		// native object for file-upload, or undefined when absent.
		const saveData = {
			...formData,
			enumValues: formData.enumValues
				? formData.enumValues.split(',').map(v => v.trim()).filter(Boolean)
				: null,
			minLength: formData.minLength !== '' ? Number.parseInt(formData.minLength, 10) : null,
			maxLength: formData.maxLength !== '' ? Number.parseInt(formData.maxLength, 10) : null,
			minimum: formData.minimum !== '' ? parseFloat(formData.minimum) : null,
			maximum: formData.maximum !== '' ? parseFloat(formData.maximum) : null,
			exclusiveMinimum: formData.exclusiveMinimum !== '' ? parseFloat(formData.exclusiveMinimum) : null,
			exclusiveMaximum: formData.exclusiveMaximum !== '' ? parseFloat(formData.exclusiveMaximum) : null,
			multipleOf: formData.multipleOf !== '' ? parseFloat(formData.multipleOf) : null,
			minItems: formData.minItems !== '' ? Number.parseInt(formData.minItems, 10) : null,
			maxItems: formData.maxItems !== '' ? Number.parseInt(formData.maxItems, 10) : null,
			xUitype: formData.xUitype || null,
			xUioptions: formData.xUioptions,
		};
		const result = onSave({ ...saveData, fieldId: field?.fieldId || null, parentFieldId: parentFieldId || null });
		if (typeof result === 'string') {
			setError(result);
		} else if (result !== false) {
			hide();
		}
	};

	const isEdit = !!field?.fieldId;
	const depth = currentDepth || 0;

	// At max depth, prevent creating further nestable types
	const availableTypes = depth >= MAX_NESTING_DEPTH - 1
		? DATA_TYPES.filter(dt => dt.value !== 'object' && dt.value !== 'array')
		: DATA_TYPES;

	const normConstraint = (v) => {
		if (v === null || v === undefined || v === '') return '';
		if (Array.isArray(v)) return v.join(', ');
		return String(v);
	};
	const CONSTRAINT_KEYS = [
		'minLength', 'maxLength', 'minimum', 'maximum',
		'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf',
		'minItems', 'maxItems', 'pattern', 'format', 'enumValues',
	];
	const constraintChanged = CONSTRAINT_KEYS.some((k) =>
		normConstraint(formData[k]) !== normConstraint(field?.[k])
	) || (formData.uniqueItems !== (field?.uniqueItems ?? false))
		|| (formData.arrayItemType !== (field?.arrayItemType || 'object'));

	const docFieldChanged = (
		formData.description !== (field?.description || '') ||
		formData.businessDefinition !== (field?.businessDefinition || '') ||
		formData.applicableStandardName !== (field?.applicableStandardName || '') ||
		formData.dataFormat !== (field?.dataFormat || '')
	);
	// xUioptions is polymorphic (string / array / object / undefined). The
	// gapD round-trip test asserts byte-for-byte preservation, so use a
	// deep JSON-equality compare for the dirty check.
	const rendererHintsChanged = (
		formData.xUitype !== (field?.xUitype || '') ||
		JSON.stringify(formData.xUioptions ?? null) !== JSON.stringify(field?.xUioptions ?? null)
	);
	const hasChanges = isEdit
		? (
			formData.dataElementName !== (field?.dataElementName || '') ||
			formData.businessTerm !== (field?.businessTerm || '') ||
			formData.mandatory !== (field?.mandatory !== undefined ? field.mandatory : true) ||
			formData.dataType !== (field?.dataType || 'string') ||
			formData.exampleValue !== formatExampleValueForInput(field?.exampleValue) ||
			docFieldChanged ||
			rendererHintsChanged ||
			constraintChanged
		)
		: (formData.dataElementName.trim() !== '' && formData.businessTerm.trim() !== '');

	const existingChildren = field?.children || [];
	const canHaveChildren = formData.dataType === 'object' || (formData.dataType === 'array' && formData.arrayItemType === 'object');

	const modalTitle = parentFieldId
		? (isEdit ? 'Edit Child Field' : 'Add Child Field')
		: (isEdit ? 'Edit Field' : 'Add New Field');

	return (
		<DSModal
			visible={visible}
			close={hide}
			variant="medium"
			dismissClickOutsideModal={false}
			addClass="dsv-field-modal"
			dataTestId="dsv-field-modal"
		>
			<DSCardConfigure>
				<DSCardConfigure.Header>
					{/* DSCardConfigure.Header.Close is silently dropped by the renderer
					    (it only renders Header.Title's children). Put the title AND the X
					    button inside Header.Title; the header's flex/space-between layout
					    splits them left/right. */}
					<DSCardConfigure.Header.Title>
						<span className="dsv-modal__title">{modalTitle}</span>
						<button
							type="button"
							className="dsv-icon-btn"
							onClick={hide}
							title="Close"
							aria-label="Close"
							data-testid="dsv-field-modal-close"
						>
							<IconClose width={16} height={16} />
						</button>
					</DSCardConfigure.Header.Title>
				</DSCardConfigure.Header>
				<DSCardConfigure.Body>
					<DSCardConfigure.Body.Col>
						{parentFieldId && (
							<p className="dsv-promote__hint" style={{ marginBottom: 12 }}>
								Adding to: <strong>{parentFieldId}</strong>
							</p>
						)}
						<div className="dsv-modal__form">
							<div className="dsv-modal__field">
								<label className="dsv-modal__label" htmlFor="dsv-field-business-term">Business Term</label>
								<input id="dsv-field-business-term" className="dsv-modal__input" type="text" value={formData.businessTerm}
									onChange={(e) => handleBusinessTermChange(e.target.value)}
									onBlur={(e) => {
										const trimmed = e.target.value.replace(/\s+$/, '');
										if (trimmed !== e.target.value) handleBusinessTermChange(trimmed);
									}}
									placeholder="e.g. Submission Entity" />
							</div>
							<div className="dsv-modal__field">
								<label className="dsv-modal__label" htmlFor="dsv-field-technical-field-name">Technical Field Name</label>
								<input id="dsv-field-technical-field-name" className="dsv-modal__input" type="text" value={formData.dataElementName}
									onChange={(e) => handleChange('dataElementName', e.target.value)}
									onBlur={(e) => {
										const trimmed = e.target.value.replace(/\s+$/, '');
										if (trimmed !== e.target.value) handleChange('dataElementName', trimmed);
									}}
									placeholder="e.g. submission_entity" />
								<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
									{isEdit
										? 'Edit only if this field needs a Technical Field Name rename.'
										: 'Auto-generated from Business Term. You can edit it if the schema needs a different Technical Field Name.'}
								</p>
							</div>
							<div className="dsv-modal__field">
								<label className="dsv-modal__label">Mandatory/Optional</label>
								<select className="dsv-modal__select" value={formData.mandatory ? 'mandatory' : 'optional'}
									onChange={(e) => handleChange('mandatory', e.target.value === 'mandatory')}>
									<option value="mandatory">Mandatory</option>
									<option value="optional">Optional</option>
								</select>
							</div>
							<div className="dsv-modal__field">
								<label className="dsv-modal__label">Data Type</label>
								<select className="dsv-modal__select" value={formData.dataType}
									onChange={(e) => handleChange('dataType', e.target.value)}>
									{availableTypes.map((dt) => (
										<option key={dt.value} value={dt.value}>{dt.name}</option>
									))}
								</select>
								{(formData.dataType === 'integer' || formData.dataType === 'number') && (
									<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
										{formData.dataType === 'integer'
											? 'Integer accepts whole numbers only (e.g. 1, 42, -7) — no decimals. Choose "number" if values can have a fractional part.'
											: 'Number accepts decimals (e.g. 3.14, 0.5, -2.0) as well as whole numbers. Choose "integer" if values must always be whole numbers.'}
									</p>
								)}
							</div>
							{formData.dataType === 'array' && (
								<div className="dsv-modal__field">
									<label className="dsv-modal__label">Array Item Type</label>
									<select className="dsv-modal__select" value={formData.arrayItemType}
										onChange={(e) => handleChange('arrayItemType', e.target.value)}>
										<option value="string">string</option>
										<option value="integer">integer</option>
										<option value="number">number</option>
										<option value="boolean">boolean</option>
										<option value="date">date</option>
										<option value="date-time">date-time</option>
										<option value="object">object</option>
										<option value="array">array</option>
									</select>
									<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
										{formData.arrayItemType === 'object'
											? 'Add child fields using the + button after saving.'
											: `Array items will be of type "${formData.arrayItemType}".`}
									</p>
								</div>
							)}
							{(formData.dataType === 'integer' || formData.dataType === 'number') && (
								<div className="dsv-modal__field">
									<label className="dsv-modal__label" htmlFor="dsv-field-numeric-format">
										Size / Precision <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span>
									</label>
									<select
										id="dsv-field-numeric-format"
										className="dsv-modal__select"
										value={formData.format}
										onChange={(e) => handleChange('format', e.target.value)}
									>
										<option value="">No preference</option>
										{formData.dataType === 'integer' ? (
											<>
												<option value="int32">Standard whole number (int32)</option>
												<option value="int64">Large whole number (int64)</option>
											</>
										) : (
											<>
												<option value="float">Decimal number (float)</option>
												<option value="double">High-precision decimal (double)</option>
											</>
										)}
									</select>
									<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
										{formData.dataType === 'integer'
											? 'Pick "Standard" for most cases — counts, ages, quantities up to about 2 billion. Pick "Large" only when values may exceed that, e.g. transaction IDs or file sizes in bytes.'
											: 'Pick "Decimal" for most cases — prices, percentages, measurements. Pick "High-precision" when many decimal places matter, e.g. scientific readings or currency conversion rates.'}
									</p>
								</div>
							)}
							{formData.dataType !== 'object' && formData.dataType !== 'array' && (
								<div className="dsv-modal__field">
									<label className="dsv-modal__label">Example Value <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span></label>
									{formData.dataType === 'boolean' ? (
										<select
											className="dsv-modal__select"
											value={formData.exampleValue}
											onChange={(e) => handleChange('exampleValue', e.target.value)}
										>
											<option value="">Select boolean value</option>
											<option value="true">true</option>
											<option value="false">false</option>
										</select>
									) : (
										<input className="dsv-modal__input" type="text" value={formData.exampleValue}
											onChange={(e) => handleChange('exampleValue', e.target.value)}
											placeholder={`e.g. ${SAMPLE_VALUES[formData.dataType] ?? 'string'}`} />
									)}
									<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
										Used in the payload preview instead of the default sample value.
									</p>
								</div>
							)}
						</div>

							{/* Data Dictionary documentation (collapsible) — five sub-fields that
								serialize into the canonical
								`<root><div><label>X</label>:<b>val</b></div>...</root>`
								description blob shared by every production DE. */}
							<details className="dsv-modal__constraints-section" open={!!(formData.description || formData.businessDefinition || formData.applicableStandardName || formData.dataFormat)}>
								<summary className="dsv-modal__constraints-toggle">Data Dictionary Documentation</summary>
								<div className="dsv-modal__constraints-body">
									<p className="dsv-promote__hint dsv-modal__documentation-help">
										These values appear when users open Data Dictionary &gt; View Details and expand this field. For example: Optional &gt; Project Contract Number.
									</p>
									<div className="dsv-modal__field">
										<label className="dsv-modal__label" htmlFor="dsv-field-business-definition">Business Definition</label>
										<textarea id="dsv-field-business-definition" className="dsv-modal__input" rows={2} value={formData.businessDefinition}
											onChange={(e) => handleChange('businessDefinition', e.target.value)}
											placeholder="Use the formal definition if it differs from the Business Term" />
										<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
											Stored in the schema documentation metadata for downstream consumers.
										</p>
									</div>
									<div className="dsv-modal__field">
										<label className="dsv-modal__label" htmlFor="dsv-field-applicable-standard">Applicable Standard Name</label>
										<input id="dsv-field-applicable-standard" className="dsv-modal__input" type="text" value={formData.applicableStandardName}
											onChange={(e) => handleChange('applicableStandardName', e.target.value)}
											placeholder="e.g. ISO 8601, BCA, NA" />
										<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
											Shown as "Applicable Standard Name". Use "NA" if no standard applies.
										</p>
									</div>
									<div className="dsv-modal__field">
										<label className="dsv-modal__label" htmlFor="dsv-field-data-format">Data Format</label>
										<input id="dsv-field-data-format" className="dsv-modal__input" type="text" value={formData.dataFormat}
											onChange={(e) => handleChange('dataFormat', e.target.value)}
											placeholder="e.g. Min characters = 1, Max characters = 255, Alphanumeric" />
										<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
											Shown as "Data Format". If left blank, the saved schema uses "NA".
										</p>
									</div>
								</div>
							</details>

						{/* Renderer Hints (x-uitype / x-uioptions) — structured per-uitype
							editors instead of raw JSON. RenderHintsForm preserves the
							native-vs-JSON-string convention each uitype expects so the
							gapD round-trip test stays green for unedited fields. */}
						<RenderHintsForm
							xUitype={formData.xUitype}
							xUioptions={formData.xUioptions}
							onChange={({ xUitype, xUioptions }) => {
								setFormData((prev) => ({ ...prev, xUitype, xUioptions }));
								setError(null);
							}}
						/>

						{/* Validation Rules (collapsible) */}
						{formData.dataType !== 'object' && (
							<details className="dsv-modal__constraints-section">
								<summary className="dsv-modal__constraints-toggle">Validation Rules</summary>
								<div className="dsv-modal__constraints-body">
									{/* String constraints */}
									{(formData.dataType === 'string' || formData.dataType === 'date' || formData.dataType === 'date-time') && (
										<>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Min Length</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.minLength}
														onChange={(e) => handleChange('minLength', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Max Length</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.maxLength}
														onChange={(e) => handleChange('maxLength', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">Pattern (regex)</label>
												<input className="dsv-modal__input" type="text" value={formData.pattern}
													onChange={(e) => handleChange('pattern', e.target.value)} placeholder="e.g. ^[A-Z]{2}\d{4}$" />
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">Format</label>
												<select className="dsv-modal__select" value={formData.format}
													onChange={(e) => handleChange('format', e.target.value)}>
													<option value="">None</option>
													<option value="email">email</option>
													<option value="uri">uri</option>
													<option value="uuid">uuid</option>
													<option value="date">date</option>
													<option value="date-time">date-time</option>
												</select>
											</div>
										</>
									)}
									{/* Integer/number constraints */}
									{(formData.dataType === 'integer' || formData.dataType === 'number') && (
										<>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Minimum</label>
													<input className="dsv-modal__input" type="number" value={formData.minimum}
														onChange={(e) => handleChange('minimum', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Maximum</label>
													<input className="dsv-modal__input" type="number" value={formData.maximum}
														onChange={(e) => handleChange('maximum', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Exclusive Minimum</label>
													<input className="dsv-modal__input" type="number" value={formData.exclusiveMinimum}
														onChange={(e) => handleChange('exclusiveMinimum', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Exclusive Maximum</label>
													<input className="dsv-modal__input" type="number" value={formData.exclusiveMaximum}
														onChange={(e) => handleChange('exclusiveMaximum', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">Multiple Of</label>
												<input className="dsv-modal__input" type="number" step="any" value={formData.multipleOf}
													onChange={(e) => handleChange('multipleOf', e.target.value)} />
											</div>
										</>
									)}
									{/* Array constraints */}
									{formData.dataType === 'array' && (
										<>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Min Items</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.minItems}
														onChange={(e) => handleChange('minItems', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Max Items</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.maxItems}
														onChange={(e) => handleChange('maxItems', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">
													<input type="checkbox" checked={formData.uniqueItems}
														onChange={(e) => handleChange('uniqueItems', e.target.checked)} style={{ marginRight: 8 }} />
													Unique Items
												</label>
											</div>
										</>
									)}
									{/* Enum (all types except object/array) */}
									{formData.dataType !== 'array' && (
										<div className="dsv-modal__field">
											<label className="dsv-modal__label">Enum Values <span style={{ fontWeight: 400, color: '#6b7280' }}>(comma-separated)</span></label>
											<input className="dsv-modal__input" type="text" value={formData.enumValues}
												onChange={(e) => handleChange('enumValues', e.target.value)} placeholder="e.g. US, CA, SG" />
										</div>
									)}
								</div>
							</details>
						)}

						{/* Children summary for existing object/array fields */}
						{isEdit && canHaveChildren && (
							<div className="dsv-modal__children-section">
								<div className="dsv-modal__children-title">
									Child Fields ({existingChildren.filter(c => c.status !== 'Deleted').length})
								</div>
								{existingChildren.filter(c => c.status !== 'Deleted').length > 0 ? (
									<div className="dsv-modal__children-list">
										{existingChildren.filter(c => c.status !== 'Deleted').map(child => (
											<div key={child.fieldId} className="dsv-modal__children-item">
												<span className="dsv-modal__children-item-key">{child.dataElementName}</span>
												<span className="dsv-modal__children-item-type">{child.dataType}</span>
												<span className={`dsv-mandatory-badge ${child.mandatory ? 'dsv-mandatory-badge--mandatory' : 'dsv-mandatory-badge--optional'}`} style={{ fontSize: 11 }}>
													{child.mandatory ? 'M' : 'O'}
												</span>
											</div>
										))}
									</div>
								) : (
									<p className="dsv-modal__children-empty">No child fields defined. Use the + button in the schema table to add children.</p>
								)}
							</div>
						)}

						{/* Hint for new object/array fields */}
						{!isEdit && canHaveChildren && (
							<p className="dsv-promote__hint" style={{ marginTop: 8 }}>
								After saving, use the + button on this field in the schema table to add child fields.
							</p>
						)}

						{error && (
							<p
								className="dsv-modal__save-error"
								data-testid="dsv-field-modal-error"
								style={{ color: '#dc2626', fontSize: 13, margin: '8px 0 0' }}
							>
								{error}
							</p>
						)}
					</DSCardConfigure.Body.Col>
				</DSCardConfigure.Body>
				<DSCardConfigure.Footer>
					<DSCardConfigure.Footer.Left />
					<DSCardConfigure.Footer.Right>
						<button className="dsv-btn dsv-btn--primary" onClick={handleSubmit} disabled={!hasChanges}>
							{isEdit ? 'Save Changes' : 'Add Field'}
						</button>
					</DSCardConfigure.Footer.Right>
				</DSCardConfigure.Footer>
			</DSCardConfigure>
		</DSModal>
	);
}

FieldModal.propTypes = { visible: PropTypes.bool, hide: PropTypes.func, field: PropTypes.object, onSave: PropTypes.func, parentFieldId: PropTypes.string, currentDepth: PropTypes.number };

// ============ Confirm Version Modal ============

const diffCellStyle = { padding: '8px 12px', border: '1px solid #e5e7eb', verticalAlign: 'top', wordBreak: 'break-word' };
const diffThStyle = { ...diffCellStyle, color: '#6b7280', fontWeight: 600, backgroundColor: '#f9fafb', textAlign: 'left' };
const diffTableStyle = { width: '100%', marginTop: 8, fontSize: 13, borderCollapse: 'collapse', tableLayout: 'fixed' };

function OrderedFieldList({ items, otherItems, side }) {
	return (
		<ol style={{ margin: 0, paddingLeft: 22, listStyle: 'decimal' }}>
			{items.map((name, i) => {
				const otherIdx = otherItems.indexOf(name);
				const isMissing = otherIdx === -1;
				const isMoved = !isMissing && otherIdx !== i;
				let background;
				let badge = null;
				let badgeColor = '#92400e';
				if (isMissing) {
					background = side === 'after' ? '#dcfce7' : '#fee2e2';
					badge = side === 'after' ? 'added' : 'removed';
					badgeColor = side === 'after' ? '#15803d' : '#b91c1c';
				} else if (isMoved) {
					background = '#fef3c7';
					badge = `was #${otherIdx + 1}`;
				}
				return (
					<li
						key={name}
						style={{
							padding: '2px 6px',
							margin: '2px 0',
							borderRadius: 3,
							backgroundColor: background,
						}}
					>
						<code style={{ fontSize: 12 }}>{name}</code>
						{badge && (
							<span style={{ marginLeft: 6, fontSize: 11, color: badgeColor, fontWeight: 600 }}>
								({badge})
							</span>
						)}
					</li>
				);
			})}
			{items.length === 0 && <li style={{ color: '#9ca3af', listStyle: 'none', marginLeft: -22 }}>—</li>}
		</ol>
	);
}

OrderedFieldList.propTypes = {
	items: PropTypes.arrayOf(PropTypes.string).isRequired,
	otherItems: PropTypes.arrayOf(PropTypes.string).isRequired,
	side: PropTypes.oneOf(['before', 'after']).isRequired,
};

const ROOT_META_LABELS = {
	securityClassification: 'Security Classification',
	sensitivityClassification: 'Sensitivity Classification',
};

function ConfirmVersionModal({ visible, hide, versionInfo, schemaDiff, payloadPreview, schemaPreview, ruleDiff, rootMetaDiff, customValidatorsDiff, fieldOrderDiff, onConfirm }) {
	const [previewTab, setPreviewTab] = useState('payload');
	if (!versionInfo) return null;

	const { elementName, currentVersion, newVersion, changeType, changeDescription } = versionInfo;

	const { added: addedFields, modified: modifiedFields, removed: deletedFields } = schemaDiff;
	const hasChanges = addedFields.length > 0 || modifiedFields.length > 0 || deletedFields.length > 0;

	const addedRules = ruleDiff?.added || [];
	const modifiedRules = ruleDiff?.modified || [];
	const removedRules = ruleDiff?.removed || [];
	const hasRuleChanges = addedRules.length > 0 || modifiedRules.length > 0 || removedRules.length > 0;

	const addedRootMeta = rootMetaDiff?.added || [];
	const modifiedRootMeta = rootMetaDiff?.modified || [];
	const removedRootMeta = rootMetaDiff?.removed || [];
	const hasRootMetaChanges = addedRootMeta.length > 0 || modifiedRootMeta.length > 0 || removedRootMeta.length > 0;

	return (
		<DSModal visible={visible} close={hide} variant="large" dismissClickOutsideModal={false} addClass="dsv-confirm-modal">
			<DSCardConfigure>
				<DSCardConfigure.Header>
					<DSCardConfigure.Header.Title>Confirm New Version</DSCardConfigure.Header.Title>
					<DSCardConfigure.Header.Close>
						<button className="dsv-icon-btn" onClick={hide} title="Close">
							<IconClose width={16} height={16} />
						</button>
					</DSCardConfigure.Header.Close>
				</DSCardConfigure.Header>
				<DSCardConfigure.Body>
					<DSCardConfigure.Body.Col>
						<div className="dsv-confirm__layout">
						<div className="dsv-confirm__pane">
						<p className="dsv-confirm__subtitle">Please review the details before creating this version.</p>

						<div className="dsv-confirm__section">
							<h3 className="dsv-confirm__section-title">Version Details</h3>
							<div className="dsv-confirm__details">
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Data Element</span>
									<span className="dsv-confirm__value">{elementName}</span>
								</div>
								{changeType !== 'INITIAL' && (
									<div className="dsv-confirm__row">
										<span className="dsv-confirm__label">Base Version</span>
										<span className="dsv-confirm__value">v{currentVersion}</span>
									</div>
								)}
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">New Version</span>
									<span className="dsv-confirm__value dsv-confirm__value--highlight">v{newVersion}</span>
								</div>
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Change Type</span>
									<span className="dsv-confirm__value">{changeType}</span>
								</div>
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Status</span>
									<span className="dsv-confirm__value">DRAFT</span>
								</div>
								{changeDescription && (
									<div className="dsv-confirm__row">
										<span className="dsv-confirm__label">Description</span>
										<span className="dsv-confirm__value">{changeDescription}</span>
									</div>
								)}
							</div>
						</div>

						<div className="dsv-confirm__section">
							<h3 className="dsv-confirm__section-title">Schema Changes</h3>

							{/* ---------- Field changes sub-section ---------- */}
							<div className="dsv-confirm__subsection" data-testid="dsv-confirm-field-diff">
								<h4 className="dsv-confirm__subsection-title">Field Changes</h4>
								{hasChanges ? (
									<div className="dsv-confirm__changes">
										{addedFields.length > 0 && (
											<>
												<div className="dsv-confirm__change-group">
													<span className="dsv-confirm__change-label dsv-confirm__change-label--added">
														+{addedFields.length} added
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
												<table style={diffTableStyle}>
													<thead>
														<tr>
															{['Technical Field Name', 'Business Term', 'Data Type', 'Mandatory'].map(h => (
																<th key={h} style={diffThStyle}>{h}</th>
															))}
														</tr>
													</thead>
													<tbody>
														{addedFields.map(f => (
															<tr key={f.fieldId}>
																<td style={{ ...diffCellStyle, fontWeight: 600, color: '#15803d' }}>{f.displayPath || f.dataElementName}</td>
																<td style={diffCellStyle}>{f.businessTerm}</td>
																<td style={diffCellStyle}>{formatDataType(f)}</td>
																<td style={diffCellStyle}>{f.mandatory ? 'Yes' : 'No'}</td>
															</tr>
														))}
													</tbody>
												</table>
												</div>
											</>
										)}
										{modifiedFields.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={addedFields.length > 0 ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--modified">
														~{modifiedFields.length} modified
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
												<table style={diffTableStyle}>
													<thead>
														<tr>
															{['Field', 'Property', 'Before', 'After'].map(h => (
																<th key={h} style={diffThStyle}>{h}</th>
															))}
														</tr>
													</thead>
													<tbody>
														{modifiedFields.map(({ field, changes }) =>
															changes.map((change, i) => (
																<tr key={`${field.fieldId}-${change.property}`}>
																	{i === 0 && (
																		<td rowSpan={changes.length} style={{ ...diffCellStyle, fontWeight: 600 }}>
																			{field.displayPath || field.dataElementName}
																		</td>
																	)}
																	<td style={diffCellStyle}>{change.property}</td>
																	<td style={{ ...diffCellStyle, color: '#dc2626', backgroundColor: '#fef2f2', textDecoration: 'line-through' }}>{change.before}</td>
																	<td style={{ ...diffCellStyle, color: '#15803d', backgroundColor: '#f0fdf4' }}>{change.after}</td>
																</tr>
															))
														)}
													</tbody>
												</table>
												</div>
											</>
										)}
										{deletedFields.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={addedFields.length > 0 || modifiedFields.length > 0 ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--deleted">
														-{deletedFields.length} removed
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
												<table style={diffTableStyle}>
													<thead>
														<tr>
															{['Technical Field Name', 'Data Type'].map(h => (
																<th key={h} style={diffThStyle}>{h}</th>
															))}
														</tr>
													</thead>
													<tbody>
														{deletedFields.map(f => (
															<tr key={f.displayPath || f.dataElementName}>
																<td style={{ ...diffCellStyle, color: '#dc2626', textDecoration: 'line-through' }}>{f.displayPath || f.dataElementName}</td>
																<td style={{ ...diffCellStyle, textDecoration: 'line-through' }}>{formatDataType(f) || '-'}</td>
															</tr>
														))}
													</tbody>
												</table>
												</div>
											</>
										)}
									</div>
								) : (
									<p className="dsv-confirm__empty">No fields were added, modified, or removed.</p>
								)}
							</div>

							{/* ---------- Root metadata changes sub-section ---------- */}
							<div className="dsv-confirm__subsection" data-testid="dsv-confirm-root-meta-diff" style={{ marginTop: 20 }}>
								<h4 className="dsv-confirm__subsection-title">Root Metadata Changes</h4>
								{hasRootMetaChanges ? (
									<div className="dsv-confirm__changes">
										{addedRootMeta.length > 0 && (
											<>
												<div className="dsv-confirm__change-group">
													<span className="dsv-confirm__change-label dsv-confirm__change-label--added">
														+{addedRootMeta.length} added
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
													<table style={diffTableStyle}>
														<thead>
															<tr>
																{['Key', 'Value'].map(h => (
																	<th key={h} style={diffThStyle}>{h}</th>
																))}
															</tr>
														</thead>
														<tbody>
															{addedRootMeta.map(entry => (
																<tr key={entry.key}>
																	<td style={{ ...diffCellStyle, fontWeight: 600, color: '#15803d' }}>{ROOT_META_LABELS[entry.key] || entry.key}</td>
																	<td style={{ ...diffCellStyle, color: '#15803d', backgroundColor: '#f0fdf4' }}>{entry.after}</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</>
										)}
										{modifiedRootMeta.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={addedRootMeta.length > 0 ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--modified">
														~{modifiedRootMeta.length} modified
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
													<table style={diffTableStyle}>
														<thead>
															<tr>
																{['Key', 'Before', 'After'].map(h => (
																	<th key={h} style={diffThStyle}>{h}</th>
																))}
															</tr>
														</thead>
														<tbody>
															{modifiedRootMeta.map(entry => (
																<tr key={entry.key}>
																	<td style={{ ...diffCellStyle, fontWeight: 600 }}>{ROOT_META_LABELS[entry.key] || entry.key}</td>
																	<td style={{ ...diffCellStyle, color: '#dc2626', backgroundColor: '#fef2f2', textDecoration: 'line-through' }}>{entry.before}</td>
																	<td style={{ ...diffCellStyle, color: '#15803d', backgroundColor: '#f0fdf4' }}>{entry.after}</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</>
										)}
										{removedRootMeta.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={(addedRootMeta.length > 0 || modifiedRootMeta.length > 0) ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--deleted">
														-{removedRootMeta.length} removed
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
													<table style={diffTableStyle}>
														<thead>
															<tr>
																{['Key', 'Previous Value'].map(h => (
																	<th key={h} style={diffThStyle}>{h}</th>
																))}
															</tr>
														</thead>
														<tbody>
															{removedRootMeta.map(entry => (
																<tr key={entry.key}>
																	<td style={{ ...diffCellStyle, color: '#dc2626', textDecoration: 'line-through' }}>{ROOT_META_LABELS[entry.key] || entry.key}</td>
																	<td style={{ ...diffCellStyle, color: '#dc2626', backgroundColor: '#fef2f2', textDecoration: 'line-through' }}>{entry.before}</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</>
										)}
									</div>
								) : (
									<p className="dsv-confirm__empty">No root-level metadata changes (classifications, additionalProperties, description, etc.).</p>
								)}
							</div>

							{/* ---------- Custom validator changes sub-section ---------- */}
							<div className="dsv-confirm__subsection" data-testid="dsv-confirm-custom-validators-diff" style={{ marginTop: 20 }}>
								<h4 className="dsv-confirm__subsection-title">Custom Validator Changes</h4>
								{(() => {
									const addedCV = customValidatorsDiff?.added || [];
									const modifiedCV = customValidatorsDiff?.modified || [];
									const removedCV = customValidatorsDiff?.removed || [];
									const hasCustomValidatorChanges = addedCV.length > 0 || modifiedCV.length > 0 || removedCV.length > 0;
									return hasCustomValidatorChanges ? (
										<div className="dsv-confirm__changes">
											{addedCV.length > 0 && (
												<>
													<div className="dsv-confirm__change-group">
														<span className="dsv-confirm__change-label dsv-confirm__change-label--added">
															+{addedCV.length} added
														</span>
													</div>
													<div className="dsv-confirm__table-wrap">
														<table style={diffTableStyle}>
															<thead>
																<tr>
																	{['Key', 'Value'].map(h => (
																		<th key={h} style={diffThStyle}>{h}</th>
																	))}
																</tr>
															</thead>
															<tbody>
																{addedCV.map(entry => (
																	<tr key={entry.key}>
																		<td style={{ ...diffCellStyle, fontWeight: 600, color: '#15803d' }}>{ROOT_META_LABELS[entry.key] || entry.key}</td>
																		<td style={{ ...diffCellStyle, color: '#15803d', backgroundColor: '#f0fdf4' }}>{entry.after}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</>
											)}
											{modifiedCV.length > 0 && (
												<>
													<div className="dsv-confirm__change-group" style={addedCV.length > 0 ? { marginTop: 16 } : undefined}>
														<span className="dsv-confirm__change-label dsv-confirm__change-label--modified">
															~{modifiedCV.length} modified
														</span>
													</div>
													<div className="dsv-confirm__table-wrap">
														<table style={diffTableStyle}>
															<thead>
																<tr>
																	{['Key', 'Before', 'After'].map(h => (
																		<th key={h} style={diffThStyle}>{h}</th>
																	))}
																</tr>
															</thead>
															<tbody>
																{modifiedCV.map(entry => (
																	<tr key={entry.key}>
																		<td style={{ ...diffCellStyle, fontWeight: 600 }}>{ROOT_META_LABELS[entry.key] || entry.key}</td>
																		<td style={{ ...diffCellStyle, color: '#dc2626', backgroundColor: '#fef2f2', textDecoration: 'line-through' }}>{entry.before}</td>
																		<td style={{ ...diffCellStyle, color: '#15803d', backgroundColor: '#f0fdf4' }}>{entry.after}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</>
											)}
											{removedCV.length > 0 && (
												<>
													<div className="dsv-confirm__change-group" style={(addedCV.length > 0 || modifiedCV.length > 0) ? { marginTop: 16 } : undefined}>
														<span className="dsv-confirm__change-label dsv-confirm__change-label--deleted">
															-{removedCV.length} removed
														</span>
													</div>
													<div className="dsv-confirm__table-wrap">
														<table style={diffTableStyle}>
															<thead>
																<tr>
																	{['Key', 'Previous Value'].map(h => (
																		<th key={h} style={diffThStyle}>{h}</th>
																	))}
																</tr>
															</thead>
															<tbody>
																{removedCV.map(entry => (
																	<tr key={entry.key}>
																		<td style={{ ...diffCellStyle, color: '#dc2626', textDecoration: 'line-through' }}>{ROOT_META_LABELS[entry.key] || entry.key}</td>
																		<td style={{ ...diffCellStyle, color: '#dc2626', backgroundColor: '#fef2f2', textDecoration: 'line-through' }}>{entry.before}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</>
											)}
										</div>
									) : (
										<p className="dsv-confirm__empty">No custom validator changes.</p>
									);
								})()}
							</div>

							{/* ---------- Field order changes sub-section ---------- */}
							{fieldOrderDiff && (fieldOrderDiff.properties || fieldOrderDiff.required) && (
								<div className="dsv-confirm__subsection"
									data-testid="dsv-confirm-field-order-diff"
									style={{ marginTop: 20 }}>
									<h4 className="dsv-confirm__subsection-title">Field Order Changes</h4>
									<p className="dsv-confirm__hint">
										JSON Schema treats these as unordered, but the form renderer displays
										fields in declared order. Review carefully.
									</p>

									{fieldOrderDiff.properties && (
										<div data-testid="dsv-confirm-properties-order">
											<h5>Properties order</h5>
											<p className="dsv-confirm__hint" style={{ marginTop: 0, marginBottom: 6 }}>
												Items highlighted in amber moved positions; green = newly added; red = removed.
											</p>
											<table style={diffTableStyle}>
												<thead><tr>
													<th style={diffThStyle}>Before</th>
													<th style={diffThStyle}>After</th>
												</tr></thead>
												<tbody><tr>
													<td style={diffCellStyle}>
														<OrderedFieldList items={fieldOrderDiff.properties.before} otherItems={fieldOrderDiff.properties.after} side="before" />
													</td>
													<td style={diffCellStyle}>
														<OrderedFieldList items={fieldOrderDiff.properties.after} otherItems={fieldOrderDiff.properties.before} side="after" />
													</td>
												</tr></tbody>
											</table>
										</div>
									)}

									{fieldOrderDiff.required && (
										<div data-testid="dsv-confirm-required-order">
											<h5>Required field order</h5>
											<table style={diffTableStyle}>
												<thead><tr>
													<th style={diffThStyle}>Before</th>
													<th style={diffThStyle}>After</th>
												</tr></thead>
												<tbody><tr>
													<td style={diffCellStyle}>
														<OrderedFieldList items={fieldOrderDiff.required.before} otherItems={fieldOrderDiff.required.after} side="before" />
													</td>
													<td style={diffCellStyle}>
														<OrderedFieldList items={fieldOrderDiff.required.after} otherItems={fieldOrderDiff.required.before} side="after" />
													</td>
												</tr></tbody>
											</table>
										</div>
									)}
								</div>
							)}

							{/* ---------- Conditional rule changes sub-section ---------- */}
							<div className="dsv-confirm__subsection" data-testid="dsv-confirm-rule-diff" style={{ marginTop: 20 }}>
								<h4 className="dsv-confirm__subsection-title">Conditional Rule Changes (x-allOf)</h4>
								{hasRuleChanges ? (
									<>
										{addedRules.length > 0 && (
											<>
												<div className="dsv-confirm__change-group">
													<span className="dsv-confirm__change-label dsv-confirm__change-label--added">
														+{addedRules.length} added
													</span>
												</div>
												<div className="dsv-confirm__rule-list">
													{addedRules.map((rule) => (
														<div key={rule.id} className="dsv-confirm__rule-card" data-testid="dsv-confirm-rule-added">
															<p className="dsv-confirm__rule-summary dsv-confirm__rule-summary--added">
																{summarizeRule(rule)}
															</p>
															<JsonViewer data={emitRule(rule)} />
														</div>
													))}
												</div>
											</>
										)}
										{modifiedRules.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={addedRules.length > 0 ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--modified">
														~{modifiedRules.length} modified
													</span>
												</div>
												<div className="dsv-confirm__rule-list">
													{modifiedRules.map(({ baseRule, rule }) => (
														<div key={rule.id} className="dsv-confirm__rule-card" data-testid="dsv-confirm-rule-modified">
															<p className="dsv-confirm__rule-summary dsv-confirm__rule-summary--modified">
																{summarizeRule(rule)}
															</p>
															<div className="dsv-confirm__rule-before-after">
																<div>
																	<p className="dsv-confirm__rule-ba-label">Before</p>
																	<JsonViewer data={emitRule(baseRule)} />
																</div>
																<div>
																	<p className="dsv-confirm__rule-ba-label">After</p>
																	<JsonViewer data={emitRule(rule)} />
																</div>
															</div>
														</div>
													))}
												</div>
											</>
										)}
										{removedRules.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={(addedRules.length > 0 || modifiedRules.length > 0) ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--deleted">
														-{removedRules.length} removed
													</span>
												</div>
												<div className="dsv-confirm__rule-list">
													{removedRules.map((rule) => (
														<div key={rule.id} className="dsv-confirm__rule-card" data-testid="dsv-confirm-rule-removed">
															<p className="dsv-confirm__rule-summary dsv-confirm__rule-summary--removed">
																{summarizeRule(rule)}
															</p>
															<JsonViewer data={emitRule(rule)} />
														</div>
													))}
												</div>
											</>
										)}
									</>
								) : (
									<p className="dsv-confirm__empty">No conditional rules were added, modified, or removed.</p>
								)}
							</div>
						</div>
						</div>
						<div className="dsv-confirm__pane dsv-confirm__pane--preview">
							<div className="dsv-update__tabs dsv-confirm__preview-tabs">
								<div
									className={`dsv-update__tab ${previewTab === 'payload' ? 'dsv-update__tab--active' : ''}`}
									onClick={() => setPreviewTab('payload')}
									data-testid="dsv-confirm-tab-payload"
								>Payload Preview</div>
								<div
									className={`dsv-update__tab ${previewTab === 'schema' ? 'dsv-update__tab--active' : ''}`}
									onClick={() => setPreviewTab('schema')}
									data-testid="dsv-confirm-tab-schema"
								>Schema Preview</div>
							</div>
							<div className="dsv-confirm__preview">
								{previewTab === 'payload'
									? <JsonViewer data={payloadPreview} />
									: <JsonViewer data={schemaPreview} />}
							</div>
						</div>
						</div>
					</DSCardConfigure.Body.Col>
				</DSCardConfigure.Body>
				<DSCardConfigure.Footer>
					<DSCardConfigure.Footer.Left />
					<DSCardConfigure.Footer.Right>
						<button className="dsv-btn dsv-btn--ghost" onClick={hide}>Cancel</button>
						<button className="dsv-btn dsv-btn--primary" style={{ marginLeft: rem(12) }} onClick={onConfirm}>Create Version</button>
					</DSCardConfigure.Footer.Right>
				</DSCardConfigure.Footer>
			</DSCardConfigure>
		</DSModal>
	);
}

ConfirmVersionModal.propTypes = {
	visible: PropTypes.bool,
	hide: PropTypes.func,
	versionInfo: PropTypes.object,
	schemaDiff: PropTypes.object,
	payloadPreview: PropTypes.object,
	schemaPreview: PropTypes.object,
	ruleDiff: PropTypes.object,
	rootMetaDiff: PropTypes.object,
	customValidatorsDiff: PropTypes.object,
	fieldOrderDiff: PropTypes.object,
	onConfirm: PropTypes.func,
};

// ============ Input Sanitization ============

/**
 * Pure helper that constructs the `uploadedRootMeta` payload from a schema
 * plus the already-extracted rules result (`extractRulesFromSchema(schema)`).
 *
 * Returns null for falsy schema so the caller can decide whether to dispatch.
 * Defensive type-guards mirror the shape that `applyUploadedRootMeta`
 * (declared inside the component) expects: title/description are string-or-null,
 * additionalProperties is boolean-or-undefined, requiredOrder is array-or-
 * undefined, residualAllOf/allOfStructure pass through verbatim (the
 * extractor already returns arrays or undefined).
 *
 * Used by BOTH the JSON-upload handler and the patch-mode useEffect so the
 * same hydration runs whether the user uploads a JSON file or opens an
 * existing version's update-schema page.
 */
export function buildRootMetaFromSchema(schema, extracted) {
  if (!schema || typeof schema !== 'object') return null;
  const safeExtracted = extracted || {};
  return {
    title: typeof schema.title === 'string' ? schema.title : null,
    description: typeof schema.description === 'string' ? schema.description : null,
    additionalProperties: typeof schema.additionalProperties === 'boolean'
      ? schema.additionalProperties
      : undefined,
    requiredOrder: Array.isArray(schema.required) ? schema.required : undefined,
    residualAllOf: safeExtracted.residualAllOf,
    allOfStructure: safeExtracted.allOfStructure,
    // Bug #2: root combinator pass-through. The builder UI has no editor
    // surface for $id, root-level anyOf, or root-level oneOf. Capture them
    // verbatim here so applyUploadedRootMeta can re-emit them on save —
    // otherwise an uploaded schema that declares conditional rules via
    // root anyOf/oneOf collapses to plain field-list semantics after the
    // next emit and the rules silently disappear.
    $id: Object.prototype.hasOwnProperty.call(schema, '$id') ? schema.$id : undefined,
    anyOf: Array.isArray(schema.anyOf) ? schema.anyOf : undefined,
    oneOf: Array.isArray(schema.oneOf) ? schema.oneOf : undefined,
    // Root object property-count constraints. The builder has no editor
    // surface for minProperties/maxProperties, so capture them verbatim
    // here and re-emit via applyUploadedRootMeta — otherwise an uploaded
    // schema's root minProperties/maxProperties silently drops on save.
    minProperties: typeof schema.minProperties === 'number' ? schema.minProperties : undefined,
    maxProperties: typeof schema.maxProperties === 'number' ? schema.maxProperties : undefined,
  };
}

export function sanitizeText(value) {
	if (typeof value !== 'string') return '';
	return value.replace(/<[^>]*>/g, '');
}

export function toDataElementId(value) {
	let id = '';
	let prevWasSep = true;
	for (let i = 0; i < value.length; i++) {
		const c = value.charCodeAt(i);
		const isLower = c >= 97 && c <= 122;
		const isUpper = c >= 65 && c <= 90;
		const isDigit = c >= 48 && c <= 57;
		if (isLower || isDigit) {
			id += value[i];
			prevWasSep = false;
		} else if (isUpper) {
			id += String.fromCharCode(c + 32);
			prevWasSep = false;
		} else if (!prevWasSep) {
			id += '_';
			prevWasSep = true;
		}
	}
	if (id.endsWith('_')) id = id.slice(0, -1);
	return id;
}

/**
 * Resolve a field's status after it is edited in the modal.
 *
 * A field added this session (status 'New') is still a brand-new, not-yet-
 * persisted field, so re-editing it keeps it 'New' (green) — it should not
 * flip to 'Modified'. Every other field (persisted: status null/'Modified'/
 * 'Deleted') becomes 'Modified' on edit. Mirrors handleDeleteField, which
 * removes 'New' fields entirely instead of marking them 'Deleted'.
 */
export function nextStatusAfterEdit(prevStatus) {
	return prevStatus === 'New' ? 'New' : 'Modified';
}

// ============ Main Page Component ============

const DSVUpdateSchemaPage = () => {
	const { goTo } = useNavigate();
	const { dataElementId } = useParams();
	// useToast is per-component state, not a shared singleton; render the
	// matching <Toast> locally so addToastMessage calls from this page
	// actually surface (the Layout-level Toast is driven by its own
	// isolated state and does not render messages raised here).
	const { toastList, addToastMessage } = useToast();
	const isBuildex = useBuildex();
	const user = useSelector((state) => state.auth.user);
	const canEditSchema = user?.role?.some((role) =>
		role.name === 'SGTradex Admin' || role.name === 'Super SGTradex Admin'
	);

	const {
		loading,
		schemaFields,
		selectedVersion,
		fetchSingleVersion,
		fetchAllVersions,
		versionHistory,
		currentVersionDetail,
		createNewVersion,
		createInitialVersions,
		promoteVersion,
		patchVersion,
		conflictError,
		setConflictError,
		impactAnalysis,
		fetchImpactAnalysis,
		fetchMasterDataTables,
	} = useDataSchemaVersioning();

	// Local state
	const [selectedTab, setSelectedTab] = useState('edit');
	// Master-data table names for the Custom Validations → Master Data Checks
	// picker. Buildex-only feature, so skip the fetch elsewhere.
	const [masterDataTableOptions, setMasterDataTableOptions] = useState([]);

	useEffect(() => {
		if (!isBuildex) return undefined;
		let cancelled = false;
		fetchMasterDataTables().then((tables) => {
			if (!cancelled) setMasterDataTableOptions(Array.isArray(tables) ? tables : []);
		});
		return () => { cancelled = true; };
	}, [isBuildex]);
	// Lifted here (not inside VersionComparison) so the chosen Base/Target survive
	// switching away from and back to the "View Changes" tab, which unmounts it.
	const [compareSelection, setCompareSelection] = useState(null);
	const [changeType, setChangeType] = useState('PATCH');
	const [changeDescription, setChangeDescription] = useState('');
	const [editingField, setEditingField] = useState(null);
	const [editingParentFieldId, setEditingParentFieldId] = useState(null);

	// Track local changes
	const [localFields, setLocalFields] = useState([]);

	// Inline validation errors
	const [formErrors, setFormErrors] = useState({});

	// Schema validation errors (ajv)
	const [schemaValidationErrors, setSchemaValidationErrors] = useState([]);

	// x-allOf conditional rules
	const [xAllOfRules, setXAllOfRules] = useState([]);

	// Custom validators stored as top-level schema keys outside JSON
	// Schema (masterDataMappings, dateCompare, sameDay, maxTotalDuration,
	// sumEquals plus any user-authored "additional" key/value pairs). The Go runtime
	// evaluates them; the editor just round-trips them.
	const [customValidators, setCustomValidators] = useState({
		masterDataMappings: null,
		dateCompare: null,
		sameDay: null,
		maxTotalDuration: null,
		sumEquals: null,
		additional: null,
	});

	// Root-level title / description captured from the last uploaded JSON
	// schema. The editor has no UI surface for these, so we stash them so
	// they round-trip into the Schema Preview + save payload (GAP-1/GAP-2).
	const [uploadedRootMeta, setUploadedRootMeta] = useState(null);

	// Bug #5: "Allow extra root fields" toggle. Default OFF (strict) so the
	// emitter sets `additionalProperties: false` on every save. ON omits the
	// keyword so the JSON Schema default of `true` applies. Hydrated from the
	// loaded schema on PATCH-mode load and from the uploaded JSON on upload.
	const [allowExtraRoot, setAllowExtraRoot] = useState(false);

	// Root-level annotation keys (no AJV impact). Empty string = unset; the
	// emitter omits the key. Hydrated from the loaded schema on PATCH-mode
	// load and from the uploaded JSON on upload.
	const [securityClassification, setSecurityClassification] = useState('');
	const [sensitivityClassification, setSensitivityClassification] = useState('');

	// x-allOf rule-level validation errors (from XAllOfBuilder). Non-empty
	// blocks save. Shape: { [ruleId]: { ifConditions?, thenFields?, ... } }
	const [xAllOfErrors, setXAllOfErrors] = useState({});

	// Track which version id's schema we already hydrated from, so refetches
	// of the same version don't clobber unsaved rule edits.
	const lastLoadedVersionIdRef = useRef(null);

	// Autosave: track updatedAt from server for optimistic locking
	const [serverUpdatedAt, setServerUpdatedAt] = useState(null);

	// Create DE mode: new data element name
	const [newDataElementName, setNewDataElementName] = useState('');

	// Autosave debounce ref
	const autosaveTimerRef = useRef(null);

	// Tracks whether the autosave-feeding state (localFields / xAllOfRules /
	// customValidators / root meta / toggles / classifications) has been
	// mutated by a genuine USER edit since the last hydration. Hydration
	// effects (initial load + reload) set this to false so they do NOT
	// trigger a PATCH; user-edit handlers set it true. Without this gate the
	// reload path re-saved the freshly-loaded schema on mount, and any save
	// could overwrite a more recent persisted edit with stale loaded state.
	const isDirtyRef = useRef(false);

	// Holds the latest performAutosave callback so the debounce-trigger effect
	// can invoke it WITHOUT listing performAutosave in its dependency array.
	// performAutosave's identity changes on every serverUpdatedAt update (it's
	// in its deps for optimistic-locking); depending on it caused an infinite
	// PATCH loop — each successful save bumped serverUpdatedAt, re-created the
	// callback, re-ran the effect, and scheduled another save.
	const performAutosaveRef = useRef(null);

	// True while a promote (DRAFT→ACTIVE etc.) is in flight. Suppresses any
	// debounced autosave from issuing a PATCH against a version that's about
	// to become non-DRAFT — the backend rejects PATCH on ACTIVE with 400.
	const isPromotingRef = useRef(false);

	// Modals (order matters for test mocking: field=0, confirm=1, promote=2, conflict=3)
	const { visible: visibleFieldModal, show: showFieldModal, hide: hideFieldModal } = useModal();
	const { visible: visibleConfirmModal, show: showConfirmModal, hide: hideConfirmModal } = useModal();
	const { visible: visiblePromoteModal, show: showPromoteModal, hide: hidePromoteModal } = useModal();
	const { visible: visibleConflictModal, show: showConflictModal, hide: hideConflictModal } = useModal();

	const elementName = selectedVersion?.elementName || currentVersionDetail?.elementName || 'Data Element';
	const currentVersion = selectedVersion?.version || currentVersionDetail?.version || '1.0.0';
	const newVersion = useMemo(
		() => computeNextVersion(currentVersion, changeType),
		[currentVersion, changeType],
	);
	const versionStatus = selectedVersion?.status || currentVersionDetail?.status;
	const isCreateMode = new URLSearchParams(window.location.search).get('mode') === 'create';
	const isEditable = canEditSchema && (isCreateMode || versionStatus === 'DRAFT');
	// Pure create mode = brand-new DE (no base version). Distinguished from
	// "create new version of existing DE" which also has isCreateMode=true but
	// hydrates selectedVersion from ?version= param.
	const isPureCreateMode = isCreateMode && !selectedVersion && !currentVersionDetail;
	const schemaLabel = isPureCreateMode
		? 'Create Data Element'
		: (isEditable ? 'Update Schema' : 'View Schema');
	const pageTitle = isPureCreateMode ? schemaLabel : `${schemaLabel}: ${elementName}`;

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{ label: '', link: '/' },
			{ label: 'Data Element', link: '/dsv' },
			// Skip the per-element crumb in pure-create — there is no element yet,
			// and `/dsv/${dataElementId}` would point at the broken `/dsv/new` route.
			...(isPureCreateMode ? [] : [{ label: elementName, link: `/dsv/${dataElementId}` }]),
			{ label: schemaLabel, link: '' },
		],
	});

	// ============ Fetch Data ============

	useEffect(() => {
		if (dataElementId) {
			const searchParams = new URLSearchParams(window.location.search);
			const version = searchParams.get('version');
			if (version) {
				fetchSingleVersion(dataElementId, version);
			} else {
				// Load all versions and pick the latest
				fetchAllVersions(dataElementId);
			}
		}
	}, [dataElementId]);

	// Populate the full version list for the View Changes comparison dropdowns
	// the first time that tab is opened (single-version loads only fetched one).
	useEffect(() => {
		if (selectedTab === 'changes' && dataElementId && (!versionHistory || versionHistory.length === 0)) {
			fetchAllVersions(dataElementId);
		}
	}, [selectedTab, dataElementId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Sync schemaFields from hook into localFields
	useEffect(() => {
		if (schemaFields && schemaFields.length > 0) {
			// Hydration, not a user edit — must not trigger autosave.
			isDirtyRef.current = false;
			setLocalFields(schemaFields.map(f => ({ ...f })));
		}
	}, [schemaFields]);

	// Set defaults from selected version — but only when the version identity
	// actually changes. Refetches of the same version (e.g. after a 200
	// autosave response) used to clobber unsaved rule edits; we gate on a ref
	// to avoid that.
	useEffect(() => {
		if (!selectedVersion) return;
		const versionKey = selectedVersion.id
			|| `${selectedVersion.dataElementId}@${selectedVersion.version}`;
		if (lastLoadedVersionIdRef.current === versionKey) return;
		lastLoadedVersionIdRef.current = versionKey;

		// Hydrating freshly-loaded version state — must not trigger autosave.
		isDirtyRef.current = false;

		// Pick the freshest complete schema. elementSchemaJson is an XML-
		// preserving clone that survives admin-corev2's @AfterLoad stripping of
		// description sub-labels, but it is NOT updated by autosave PATCHes, so
		// preferring it unconditionally resurrected pre-edit (stale) state on
		// reload — the root cause of field-type / constraint / x-allOf edits
		// appearing to revert. pickHydrationSchema prefers the authoritative
		// elementSchema and only falls back to the clone when elementSchema is
		// genuinely description-stripped.
		const schema = pickHydrationSchema(selectedVersion.elementSchema, selectedVersion.elementSchemaJson);
		if (schema && localFields.length === 0) {
			setLocalFields(extractSchemaFields(schema));
		}

		// Hydrate xAllOf rules AND the root-meta harness used by applyUploadedRootMeta
		// so PATCH-mode loads behave identically to JSON-upload loads (F2 + F3). Without
		// this, uploadedRootMeta stays null on PATCH and applyUploadedRootMeta short-
		// circuits, so required[] / allOf[] source order is lost on save.
		const extracted = schema
			? extractRulesFromSchema(schema)
			: { rules: [], residualAllOf: [], allOfStructure: [] };
		setXAllOfRules(extracted.rules || []);
		if (schema) {
			setUploadedRootMeta(buildRootMetaFromSchema(schema, extracted));
		}

		// Bug #5: hydrate "Allow extra root fields" toggle from the loaded schema.
		// `additionalProperties: true` (or absent on a freshly-loaded schema with
		// no opinion) maps to ON. Explicit `false` maps to OFF (strict).
		if (schema && typeof schema.additionalProperties === 'boolean') {
			setAllowExtraRoot(schema.additionalProperties === true);
		}

		setSecurityClassification(
			typeof schema?.securityClassification === 'string' ? schema.securityClassification : '',
		);
		setSensitivityClassification(
			typeof schema?.sensitivityClassification === 'string' ? schema.sensitivityClassification : '',
		);

		// Hydrate custom validators (masterDataMappings, dateCompare,
		// sameDay, maxTotalDuration, sumEquals) from the schema's top-level keys.
		setCustomValidators(extractCustomValidators(schema));
	}, [selectedVersion]);

	// Track updatedAt from fetched version
	useEffect(() => {
		const version = selectedVersion || currentVersionDetail;
		if (version?.updatedAt) {
			setServerUpdatedAt(version.updatedAt);
		}
	}, [selectedVersion, currentVersionDetail]);

	// Fetch impact analysis when we have version info
	useEffect(() => {
		if (dataElementId && currentVersion) {
			fetchImpactAnalysis(dataElementId, currentVersion);
		}
	}, [dataElementId, currentVersion]);

	// Show conflict modal when conflict error is detected
	useEffect(() => {
		if (conflictError) {
			showConflictModal();
		}
	}, [conflictError]);

	// Apply the custom-validators editor state to a built schema.
	// Sanitizes (drops half-filled rules) and either sets or deletes
	// each top-level key so the preview + save payload always reflect
	// what the user authored.
	function applyCustomValidators(schema) {
		if (!schema || typeof schema !== 'object') return schema;
		const sanitized = sanitizeCustomValidators(customValidators);
		const out = { ...schema };
		applyCustomValidatorsToSchema(out, sanitized);
		return out;
	}

	// Overlay captured root `title` / `description` from the last JSON
	// upload so they round-trip into the preview and save payload (GAP-1/2).
	// The editor has no form surface for those fields. `residualAllOf`
	// captures non-rule `allOf` entries from the uploaded JSON (e.g.
	// `{anyOf: ...}` blocks) so they survive next to any rule entries the
	// editor emits via buildJsonSchema.
	function applyUploadedRootMeta(schema) {
		if (!uploadedRootMeta || !schema) return schema;
		const out = { ...schema };
		if (uploadedRootMeta.title != null) out.title = uploadedRootMeta.title;
		if (uploadedRootMeta.description != null) out.description = uploadedRootMeta.description;
		if (typeof uploadedRootMeta.additionalProperties === 'boolean') {
			out.additionalProperties = uploadedRootMeta.additionalProperties;
		}
		// Bug #2: round-trip root $id / anyOf / oneOf verbatim. The builder
		// has no editor surface for these so the only way to preserve them
		// from upload through save is to overlay them here. Without this,
		// uploading a schema with root combinators yields a saved schema
		// that no longer enforces them.
		if (uploadedRootMeta.$id !== undefined) out.$id = uploadedRootMeta.$id;
		if (Array.isArray(uploadedRootMeta.anyOf)) out.anyOf = uploadedRootMeta.anyOf;
		if (Array.isArray(uploadedRootMeta.oneOf)) out.oneOf = uploadedRootMeta.oneOf;
		// Re-emit root object property-count constraints captured at upload
		// (no editor surface). Without this they drop on the next emit.
		if (typeof uploadedRootMeta.minProperties === 'number') out.minProperties = uploadedRootMeta.minProperties;
		if (typeof uploadedRootMeta.maxProperties === 'number') out.maxProperties = uploadedRootMeta.maxProperties;
		// Re-sort root `required` to follow source's authoring order. The
		// builder emits required[] in properties-iteration order, which can
		// diverge from source on schemas where required[] isn't a strict
		// prefix of properties (B3).
		if (Array.isArray(out.required) && Array.isArray(uploadedRootMeta.requiredOrder)) {
			out.required = reorderRequired(out.required, uploadedRootMeta.requiredOrder);
		}
		if (Array.isArray(uploadedRootMeta.residualAllOf)) {
			const baseAllOf = Array.isArray(out.allOf) ? out.allOf : [];
			const ruleEntriesFromBuilder = baseAllOf.filter((entry) => {
				if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
				const keys = Object.keys(entry);
				if (!keys.includes('if')) return false;
				if (!keys.includes('then') && !keys.includes('else')) return false;
				return keys.every((k) => k === 'if' || k === 'then' || k === 'else');
			});
			// Walk the captured `allOfStructure` (source's per-entry kind tags)
			// and substitute freshly-built rule entries into their original
			// rule slots. Falls back to the legacy [...residual, ...rules]
			// concat when allOfStructure is absent (B4).
			const structure = Array.isArray(uploadedRootMeta.allOfStructure)
				? uploadedRootMeta.allOfStructure
				: null;
			let merged;
			if (structure) {
				let ruleIdx = 0;
				merged = structure
					.map((slot) => {
						if (slot?.kind === 'rule') {
							return ruleEntriesFromBuilder[ruleIdx++] ?? null;
						}
						return slot?.value ?? null;
					})
					.filter((entry) => entry !== null);
				// Append any extra rule entries the editor produced that
				// don't have a source slot (e.g. user authored a new rule
				// in the editor after upload).
				while (ruleIdx < ruleEntriesFromBuilder.length) {
					merged.push(ruleEntriesFromBuilder[ruleIdx++]);
				}
			} else {
				merged = [...uploadedRootMeta.residualAllOf, ...ruleEntriesFromBuilder];
			}
			if (merged.length > 0) out.allOf = merged;
			else delete out.allOf;
		}
		return out;
	}

	// Bug #5: overlay the "Allow extra root fields" toggle onto an emitted
	// schema. The toggle is authoritative — it overrides any `additionalProperties`
	// the upstream emit produced (whether from baseSchema spread or
	// applyUploadedRootMeta). OFF (default) → additionalProperties:false; ON →
	// keyword omitted so JSON Schema's default `true` applies.
	function applyAllowExtraRoot(schema) {
		if (!schema || typeof schema !== 'object') return schema;
		const out = { ...schema };
		if (allowExtraRoot) {
			delete out.additionalProperties;
		} else {
			out.additionalProperties = false;
		}
		return out;
	}

	// Overlay the two root-level classification dropdowns. Authoritative:
	// when the user clears a value, the key is dropped from the emit even if
	// baseSchema carried one. Annotation-only — AJV ignores both keys.
	function applyRootClassifications(schema) {
		if (!schema || typeof schema !== 'object') return schema;
		const out = { ...schema };
		if (securityClassification) {
			out.securityClassification = securityClassification;
		} else {
			delete out.securityClassification;
		}
		if (sensitivityClassification) {
			out.sensitivityClassification = sensitivityClassification;
		} else {
			delete out.sensitivityClassification;
		}
		return out;
	}

	// Autosave: debounced PATCH when localFields change (DRAFT only).
	const AUTOSAVE_ENABLED = true;
	const performAutosave = useCallback(async () => {
		if (!AUTOSAVE_ENABLED) return;
		if (isPromotingRef.current) return;
		if (!isEditable || versionStatus !== 'DRAFT' || !dataElementId || isCreateMode) return;

		const baseSchema = selectedVersion?.elementSchemaJson || selectedVersion?.elementSchema;
		const currentSchema = applyRootClassifications(applyAllowExtraRoot(applyCustomValidators(applyUploadedRootMeta(buildJsonSchemaFromRules(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchema,
			xAllOfRules,
		)))));

		const payload = {
			schema: currentSchema,
			updatedAt: serverUpdatedAt,
		};

		const result = await patchVersion(dataElementId, currentVersion, payload);
		if (result && !result.error) {
			// The persisted state now matches what we just sent — clear the
			// dirty flag so the trigger effect doesn't loop on the
			// serverUpdatedAt change below.
			isDirtyRef.current = false;
			if (result.updatedAt) {
				setServerUpdatedAt(result.updatedAt);
			}
		} else if (result?.conflict) {
			// Conflict modal will show via the effect above
		}
	}, [localFields, serverUpdatedAt, dataElementId, currentVersion, versionStatus, isEditable, isCreateMode, selectedVersion, xAllOfRules, customValidators, uploadedRootMeta, allowExtraRoot, securityClassification, sensitivityClassification]);

	// Keep the ref pointing at the freshest performAutosave closure so the
	// trigger effect can call it without depending on its (frequently
	// changing) identity.
	performAutosaveRef.current = performAutosave;

	// Trigger autosave with a 2s debounce — but ONLY when a real user edit has
	// marked the state dirty. Hydration (initial load / reload) sets state
	// without flipping isDirtyRef, so it does not schedule a PATCH. The effect
	// depends on the autosave-feeding CONTENT (not on performAutosave), so each
	// distinct user edit reschedules the debounce, while serverUpdatedAt bumps
	// from a completed save no longer retrigger it.
	useEffect(() => {
		if (!AUTOSAVE_ENABLED) return;
		if (!isEditable || versionStatus !== 'DRAFT' || isCreateMode) return;
		if (!isDirtyRef.current) return;
		if (autosaveTimerRef.current) {
			clearTimeout(autosaveTimerRef.current);
		}
		autosaveTimerRef.current = setTimeout(() => {
			performAutosaveRef.current?.();
		}, 2000);
		return () => {
			if (autosaveTimerRef.current) {
				clearTimeout(autosaveTimerRef.current);
			}
		};
	}, [localFields, xAllOfRules, customValidators, uploadedRootMeta, allowExtraRoot, securityClassification, sensitivityClassification, isEditable, versionStatus, isCreateMode]);

	// ============ Handlers ============

	function handleBackToElement() {
		// Pure-create has no element to return to — `dataElementId` is the route sentinel 'new'.
		goTo(isPureCreateMode ? '/dsv' : `/dsv/${dataElementId}`);
	}

	function handleJsonUpload(parsedSchema) {
		if (!parsedSchema || typeof parsedSchema !== 'object') {
			addToastMessage('Uploaded file does not contain a valid schema object', 'error');
			return;
		}
		const fields = extractSchemaFields(parsedSchema);
		if (fields.length === 0) {
			addToastMessage('Uploaded schema has no properties to extract', 'error');
			return;
		}
		// Uploading a JSON schema is a user edit — allow autosave to persist it.
		isDirtyRef.current = true;
		setLocalFields(fields.map(f => ({ ...f, status: 'New' })));

		// Parse conditional rules from BOTH `x-allOf` and `allOf` in the
		// uploaded schema so existing DE schemas surface in the rule builder.
		// Non-rule `allOf` entries (e.g. `{anyOf: ...}` blocks) are captured
		// as `residualAllOf` so applyUploadedRootMeta can re-emit them next
		// to any rules the editor produces. `allOfStructure` records each
		// source `allOf` slot's kind (rule vs residual) so the writer can
		// preserve source's interleaving order on save (B4).
		const {
			rules: uploadedRules,
			residualAllOf: uploadedResidualAllOf,
			allOfStructure: uploadedAllOfStructure,
		} = extractRulesFromSchema(parsedSchema);

		setUploadedRootMeta(buildRootMetaFromSchema(parsedSchema, {
			residualAllOf: uploadedResidualAllOf,
			allOfStructure: uploadedAllOfStructure,
		}));

		// Bug #5: hydrate the "Allow extra root fields" toggle from the uploaded
		// JSON. Explicit boolean → toggle reflects it; absent → leave the toggle
		// at its current state (defaults to OFF on a fresh create-mode page).
		if (typeof parsedSchema.additionalProperties === 'boolean') {
			setAllowExtraRoot(parsedSchema.additionalProperties === true);
		}

		if (typeof parsedSchema.securityClassification === 'string') {
			setSecurityClassification(parsedSchema.securityClassification);
		}
		if (typeof parsedSchema.sensitivityClassification === 'string') {
			setSensitivityClassification(parsedSchema.sensitivityClassification);
		}

		setXAllOfRules(uploadedRules);
		// Pull custom validators out of the uploaded schema too — every
		// production schema carries at least `masterDataMappings`.
		setCustomValidators(extractCustomValidators(parsedSchema));

		const ruleNote = uploadedRules.length > 0
			? ` and ${uploadedRules.length} conditional rule${uploadedRules.length === 1 ? '' : 's'}`
			: '';
		addToastMessage(`Schema fields${ruleNote} populated from uploaded JSON`, 'success');
	}

	function handleConflictReload() {
		hideConflictModal();
		setConflictError(null);
		const searchParams = new URLSearchParams(window.location.search);
		const version = searchParams.get('version');
		if (version) {
			fetchSingleVersion(dataElementId, version);
		} else {
			fetchAllVersions(dataElementId);
		}
	}

	function handleAddField() {
		setEditingField(null);
		setEditingParentFieldId(null);
		showFieldModal();
	}

	function handleAddChildField(parentField) {
		const depth = getFieldDepth(parentField.fieldId) + 1;
		if (depth >= MAX_NESTING_DEPTH) {
			addToastMessage(`Maximum nesting depth (${MAX_NESTING_DEPTH} levels) reached`, 'error');
			return;
		}
		setEditingField(null);
		setEditingParentFieldId(parentField.fieldId);
		showFieldModal();
	}

	function handleEditField(field) {
		setEditingField(field);
		setEditingParentFieldId(field.parentKey || null);
		showFieldModal();
	}

	function handleMoveFieldUp(field) {
		isDirtyRef.current = true;
		setLocalFields(prev => moveFieldInTree(prev, field.fieldId, 'up'));
	}

	function handleMoveFieldDown(field) {
		isDirtyRef.current = true;
		setLocalFields(prev => moveFieldInTree(prev, field.fieldId, 'down'));
	}

	function handleDeleteField(field) {
		if (window.confirm(`Are you sure you want to delete "${field.dataElementName}"?`)) {
			isDirtyRef.current = true;
			if (field.status === 'New') {
				// New fields added this session — remove entirely
				setLocalFields(prev => removeFieldFromTree(prev, field.fieldId));
			} else {
				// Existing fields — mark as deleted (strikethrough)
				setLocalFields(prev => updateFieldInTree(prev, field.fieldId, f => ({ ...f, status: 'Deleted' })));
			}
		}
	}

	function handleSaveField(formData) {
		const isPrimitiveType = formData.dataType !== 'object' && formData.dataType !== 'array';
		const parsedExampleValue = isPrimitiveType
			? parseExampleValue(formData.exampleValue, formData.dataType)
			: { value: '' };
		if (parsedExampleValue.error) {
			return parsedExampleValue.error;
		}

		const sanitized = {
			...formData,
			dataElementName: sanitizeText(formData.dataElementName),
			businessTerm: sanitizeText(formData.businessTerm),
			exampleValue: parsedExampleValue.value,
		};

		if (!sanitized.dataElementName || !sanitized.businessTerm) {
			return 'Business Term and Technical Field Name are required';
		}

		const parentId = sanitized.parentFieldId || editingParentFieldId;

		// Check for duplicate technical field name among siblings
		const siblings = parentId
			? (function findChildren(fields, pid) {
				for (const f of fields) {
					if (f.fieldId === pid) return f.children || [];
					if (f.children?.length > 0) { const found = findChildren(f.children, pid); if (found) return found; }
				}
				return null;
			})(localFields, parentId) || []
			: localFields;
		const duplicateKey = siblings.some(f =>
			f.fieldId !== sanitized.fieldId &&
			f.status !== 'Deleted' &&
			f.dataElementName === sanitized.dataElementName
		);
		if (duplicateKey) {
			return `Duplicate key: "${sanitized.dataElementName}" already exists at this level`;
		}

		// Block self-contradictory or unsafe constraints (min>max bounds, invalid
		// regex, unsafe date/date-time length limits). formData already carries
		// parsed numeric bounds / nulls, so it feeds the shared validator directly.
		// Returning the first descriptive message reuses the existing inline-error
		// mechanism (FieldModal calls setError on a string result and aborts).
		const constraintErrors = getFieldConstraintErrors(sanitized);
		if (constraintErrors.length > 0) {
			return constraintErrors[0].message;
		}

		// A field add/edit is a genuine user edit — allow autosave to fire.
		isDirtyRef.current = true;

		if (sanitized.fieldId) {
			// Edit existing field at any depth. Spread the existing field FIRST so
			// round-trip metadata the modal doesn't expose (descriptionIsStructured,
			// originalDescriptionBlob, descriptionExplicit, exampleKeyword, nullable,
			// constValue/Explicit, uniqueItemsExplicit, titleExplicit, typeExplicit,
			// additionalProperties, itemsRawMeta, itemsAdditionalProperties,
			// requiredOrder, nestedAllOf/XAllOf, innerArraySchema) survives the save.
			// Without the spread, every modal Save silently dropped these and emit
			// fell back to defaults — collapsing XML descriptions to plain strings,
			// re-emitting `attachments` titles, losing nested allOf rules, etc.
			// arrayItemType is force-nulled for non-array types so FormModal's
			// default 'object' value doesn't get stamped onto integer/string fields.
			setLocalFields(prev => updateFieldInTree(prev, sanitized.fieldId, f => ({
				...f,
				...sanitized,
				arrayItemType: sanitized.dataType === 'array'
					? (sanitized.arrayItemType || 'object')
					: null,
				children: f.children || [],
				parentKey: f.parentKey,
				// Keep a session-new field 'New' on re-edit; only persisted
				// fields become 'Modified'. See nextStatusAfterEdit.
				status: nextStatusAfterEdit(f.status),
			})));
		} else if (parentId) {
			// Add new child field
			const childFieldId = `${parentId}.${sanitized.dataElementName}`;
			const newChild = {
				...sanitized,
				fieldId: childFieldId,
				parentKey: parentId,
				children: [],
				status: 'New',
			};
			setLocalFields(prev => addChildToField(prev, parentId, newChild));
		} else {
			// Add new root field — use technical field name as fieldId for readable child paths
			const newField = {
				...sanitized,
				fieldId: sanitized.dataElementName || `new-${Date.now()}`,
				parentKey: null,
				children: [],
				status: 'New',
			};
			setLocalFields(prev => [...prev, newField]);
		}

		setEditingParentFieldId(null);
	}

	/**
	 * Show confirmation modal before creating version.
	 * Validates required fields first, then shows the modal for review.
	 */
	function handleConfirmChanges() {
		const errors = {};

		if (isPureCreateMode && !newDataElementName.trim()) {
			errors.newDataElementName = 'Data Element Name is required';
		}

		if (!changeDescription.trim()) {
			errors.changeDescription = 'This field is required';
		}

		if (isEditable) {
			if (!securityClassification) {
				errors.securityClassification = 'Security Classification is required';
			}
			if (!sensitivityClassification) {
				errors.sensitivityClassification = 'Sensitivity Classification is required';
			}
		}

		setFormErrors(errors);

		if (Object.keys(errors).length > 0) {
			return;
		}

		// Block confirmation when the auto-derived next version could not be computed.
		// Skipped in pure-create mode (uses hard-coded v1.0.0 / INITIAL on submit).
		// Reaching this branch means currentVersion is not strict M.m.p — refuse to POST a blank version.
		if (!isPureCreateMode && !newVersion) {
			addToastMessage('Could not derive next version from the current version. Please reload and try again.', 'error');
			return;
		}

		// Block save when any x-allOf rule has validation errors (empty
		// ifConditions, dangling refs, invalid regex, bad bounds, duplicates).
		// XAllOfBuilder's useRuleValidation hook keeps xAllOfErrors in sync.
		if (xAllOfErrors && Object.keys(xAllOfErrors).length > 0) {
			addToastMessage(
				'Conditional rules have errors. Fix them before saving.',
				'error',
			);
			return;
		}

		// Whole-schema catch-all: a base field with self-contradictory / unsafe
		// constraints (e.g. minLength > maxLength) must not slip through even if
		// the user never re-opens its editor. Names the first few offending fields
		// (with the leading reason) and a count when more remain.
		const constraintProblems = getSchemaConstraintErrors(localFields);
		if (constraintProblems.length > 0) {
			const named = constraintProblems.slice(0, 3).map(p => p.path).join(', ');
			const overflow = constraintProblems.length > 3
				? ` (and ${constraintProblems.length - 3} more)`
				: '';
			const firstReason = constraintProblems[0].errors[0].message;
			addToastMessage(
				`Field constraint errors on: ${named}${overflow}. ${firstReason} Fix them before saving.`,
				'error',
			);
			return;
		}

		// Validate schema shape with ajv before allowing confirm. "Create new
		// version of existing DE" (`?mode=create&version=X`) still needs the
		// source as base so the emitter can spread item-level keywords, nested
		// allOf, additionalProperties, etc. through. Only pure-create (brand-
		// new DE, no selectedVersion) emits without a base. Mirrors the gate
		// used in handleSubmitVersion and finalSchema below.
		const baseSchema = isPureCreateMode
			? undefined
			: (selectedVersion?.elementSchemaJson || selectedVersion?.elementSchema);
		const currentSchema = applyRootClassifications(applyAllowExtraRoot(applyCustomValidators(applyUploadedRootMeta(buildJsonSchemaFromRules(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchema,
			xAllOfRules,
		)))));
		const shapeResult = validateSchemaShape(currentSchema);
		if (!shapeResult.valid) {
			setSchemaValidationErrors(shapeResult.errors);
			addToastMessage('Schema has structural errors. Please fix them before saving.', 'error');
			return;
		}
		setSchemaValidationErrors([]);

		// Creating any non-buildex version (pure-create AND new-version-of-existing, both
		// of which are mode=create) persists a pull-side query contract, so the generated
		// querySchema must have at least one required scalar — it becomes the mandatory
		// pull-side query key. Without it, downstream pulls cannot be satisfied (pitstop-core
		// rejects empty query params). Scoped to create mode so editing a legacy DRAFT that
		// predates this rule isn't locked out; the admin-corev2 guard re-derives querySchema
		// regardless. Buildex tenants only push (no pull flow), so the gate is skipped — the
		// querySchema column is nullable end-to-end (admin-corev2 + pitstop-core).
		if (isCreateMode && !isBuildex) {
			const querySchemaPreview = buildQuerySchemaFromElementSchema(currentSchema);
			if (!Array.isArray(querySchemaPreview.required) || querySchemaPreview.required.length === 0) {
				addToastMessage(
					'At least one required scalar field (string / number / integer / boolean) must be defined. It becomes the mandatory query key for pulling this data element.',
					'error'
				);
				return;
			}
		}

		// Sanitize text inputs before showing confirmation
		setChangeDescription(sanitizeText(changeDescription));

		showConfirmModal();
	}

	/**
	 * Actually creates the new version after user confirms in the modal.
	 * POST /data-element-versions/:dataElementId/versions
	 */
	async function handleSubmitVersion() {
		hideConfirmModal();

		const baseSchema = selectedVersion?.elementSchemaJson || selectedVersion?.elementSchema;
		const newSchema = applyRootClassifications(applyAllowExtraRoot(applyCustomValidators(applyUploadedRootMeta(buildJsonSchemaFromRules(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchema,
			xAllOfRules,
		)))));

		if (isPureCreateMode) {
			const trimmedName = (newDataElementName || '').trim();
			if (!trimmedName) {
				addToastMessage('Data Element Name is required', 'error');
				return;
			}
			const newId = toDataElementId(trimmedName);
			if (!newId) {
				addToastMessage('Data Element Name must contain alphanumeric characters', 'error');
				return;
			}
			const createPayload = [{
				id: newId,
				name: trimmedName,
				schema: newSchema,
				...(isBuildex ? {} : { querySchema: buildQuerySchemaFromElementSchema(newSchema) }),
				version: '1.0.0',
				changeType: 'INITIAL',
				changeDescription: changeDescription,
				status: 'DRAFT',
				type: 'DOCUMENT',
			}];

			const result = await createInitialVersions(createPayload);
			if (result?.error) {
				addToastMessage(typeof result.error === 'string' ? result.error : 'Failed to create data element', 'error');
				return;
			}
			addToastMessage('Data Element created successfully', 'success');
			goTo(`/dsv/${newId}`);
			return;
		}

		// Defense in depth: handleConfirmChanges already gates the modal on this,
		// but never POST a payload with a blank version even if the modal flow is bypassed.
		if (!newVersion) {
			addToastMessage('Could not derive next version. Please reload and try again.', 'error');
			return;
		}

		const payload = {
			name: elementName,
			schema: newSchema,
			// Derive the pull-side query contract from the EDITED schema. Copying the
			// parent version's querySchema leaves it stale whenever this version changes
			// which fields are required, corrupting downstream pull validation. Buildex is
			// push-only, so omit querySchema entirely (nullable end-to-end) — matching the
			// pure-create path above.
			...(isBuildex ? {} : { querySchema: buildQuerySchemaFromElementSchema(newSchema) }),
			version: newVersion,
			changeType: changeType,
			changeDescription: changeDescription,
			status: 'DRAFT',
			type: selectedVersion?.type || currentVersionDetail?.type || 'DOCUMENT',
		};

		const result = await createNewVersion(dataElementId, payload);

		if (result?.error) {
			addToastMessage(typeof result.error === 'string' ? result.error : 'Failed to create version', 'error');
			return;
		}

		addToastMessage('Version created successfully', 'success');
		goTo(`/dsv/${dataElementId}`);
	}

	// ============ Promote Handlers ============

	const isVersionDataLoaded = !!(selectedVersion || currentVersionDetail);
	const isPageReady = isCreateMode ? !loading : (!loading && isVersionDataLoaded);
	const transitionConfig = !isCreateMode ? getTransitionConfig(versionStatus) : null;
	const disabledReason = transitionConfig && selectedVersion ? transitionConfig.getDisabledReason(selectedVersion) : null;
	const enabledHint = transitionConfig && selectedVersion ? transitionConfig.getEnabledHint(selectedVersion) : null;

	async function handleConfirmPromote(targetStatus, expiryDate) {
		hidePromoteModal();
		const config = getTransitionConfig(versionStatus);
		// Cancel any pending debounced autosave and block new ones from
		// firing while promote is in flight. Without this guard a debounce
		// timer scheduled by a recent edit can fire AFTER promote succeeds,
		// triggering a PATCH against a now-ACTIVE version (backend 400).
		if (autosaveTimerRef.current) {
			clearTimeout(autosaveTimerRef.current);
			autosaveTimerRef.current = null;
		}
		isPromotingRef.current = true;
		try {
			const result = await promoteVersion(dataElementId, currentVersion, targetStatus, expiryDate);
			if (result?.error) {
				addToastMessage(
					typeof result.error === 'string' ? result.error : `Failed to promote v${currentVersion}`,
					'error'
				);
				return;
			}
			addToastMessage(`Version ${currentVersion} ${config?.verb || 'promoted'}`, 'success');
			goTo(`/dsv/${dataElementId}`);
		} finally {
			isPromotingRef.current = false;
		}
	}

	const tabs = [
		{ key: 'edit', label: isEditable ? 'Edit Schema' : 'Schema' },
		{ key: 'customValidators', label: 'Custom Validations' },
		{ key: 'json', label: 'Payload Preview' },
		{ key: 'schema', label: 'Schema Preview' },
		{ key: 'validate', label: 'Validate' },
		...(isEditable ? [
			{ key: 'changes', label: 'View Changes' },
			{ key: 'impact', label: 'Impact Analysis' },
		] : []),
	];

	// Resolve the baseline schema once. Only pure create (brand-new DE) has
	// no base to diff against — "create new version of existing DE"
	// (`?mode=create&version=X`) hydrates selectedVersion and SHOULD diff
	// against it, otherwise the Confirm modal treats every field as "added"
	// and never shows removals/modifications.
	const baseSchemaForDiff = isPureCreateMode
		? undefined
		: (selectedVersion?.elementSchemaJson || selectedVersion?.elementSchema);

	// Single source of truth for the emitted JSON Schema: reused by
	// JsonDownload, the Schema Preview tab, and the confirm modal.
	// applyAllowExtraRoot is the final overlay so the toggle is authoritative
	// over any `additionalProperties` produced by baseSchema spread or the
	// uploadedRootMeta overlay.
	const finalSchema = useMemo(
		() => applyRootClassifications(applyAllowExtraRoot(applyCustomValidators(applyUploadedRootMeta(buildJsonSchemaFromRules(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchemaForDiff,
			xAllOfRules,
		))))),
		[localFields, baseSchemaForDiff, xAllOfRules, uploadedRootMeta, customValidators, allowExtraRoot, securityClassification, sensitivityClassification],
	);

	// Stable working-copy reference for the comparison so its diff only recomputes
	// when the live schema actually changes (not on every parent render).
	const compareWorkingCopy = useMemo(
		() => ({ label: 'Working copy (unsaved)', schema: finalSchema }),
		[finalSchema],
	);

	// Rule-level diff for the confirm modal + View Changes tab. In create mode
	// there is no baseline so every rule shows as "added" — which is the
	// correct user-facing framing.
	const ruleDiff = useMemo(
		() => diffXAllOfRules(xAllOfRules, baseSchemaForDiff),
		[xAllOfRules, baseSchemaForDiff],
	);
	// Root-level metadata diff: classifications, additionalProperties toggle,
	// description / title / $id / root anyOf|oneOf. Closes the gap where the
	// Confirm modal previously hid these changes from reviewers.
	const rootMetaDiff = useMemo(
		() => diffRootMetadata(finalSchema, baseSchemaForDiff),
		[finalSchema, baseSchemaForDiff],
	);
	const customValidatorsDiff = useMemo(
		() => diffCustomValidators(finalSchema, baseSchemaForDiff),
		[finalSchema, baseSchemaForDiff],
	);
	const fieldOrderDiff = useMemo(
		() => diffFieldOrder(finalSchema, baseSchemaForDiff),
		[finalSchema, baseSchemaForDiff],
	);
	// Collect all changed fields recursively with human-readable display path
	function collectChangedFields(fields, parentPath) {
		const result = [];
		for (const f of fields) {
			const displayPath = parentPath ? `${parentPath}.${f.dataElementName}` : f.dataElementName;
			if (f.status) result.push({ ...f, displayPath });
			if (f.children && f.children.length > 0) {
				result.push(...collectChangedFields(f.children, displayPath));
			}
		}
		return result;
	}
	const changedFields = collectChangedFields(localFields, '');
	const hasFormChanges = isPureCreateMode
		? (newDataElementName.trim() !== '' || changeDescription.trim() !== '' || changedFields.length > 0)
		: (changeDescription.trim() !== '' || changedFields.length > 0);


	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			{/* Back link */}
			<div
				className="dsv-detail__back"
				role="button"
				tabIndex={0}
				onClick={handleBackToElement}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						handleBackToElement();
					}
				}}
			>
				<span className="dsv-detail__back-arrow">&larr;</span>
				<span>Back to Element</span>
			</div>

			{/* Create DE Name Input */}
			{isPureCreateMode && isEditable && (
				<div className="dsv-update__create-de" style={{ marginBottom: 16 }}>
					<label className="dsv-update__version-label">Data Element Name</label>
					<input
						className={`dsv-modal__input ${formErrors.newDataElementName ? 'dsv-modal__input--error' : ''}`}
						type="text"
						maxLength={200}
						value={newDataElementName}
						onChange={(e) => {
							setNewDataElementName(e.target.value);
							setFormErrors(prev => ({ ...prev, newDataElementName: undefined }));
						}}
						placeholder="Enter new data element name"
						data-testid="create-de-name-input"
					/>
					{formErrors.newDataElementName && (
						<span className="dsv-form-error">{formErrors.newDataElementName}</span>
					)}
				</div>
			)}

			{/* Header */}
			<div className="dsv-update__header">
				<div className="dsv-update__header-left">
					<h1 className="dsv-update__title">{pageTitle}</h1>
					<div className="dsv-update__subtitle">
						{isPureCreateMode
							? 'Define fields and conditional rules for the new data element. The first version will be v1.0.0.'
							: isEditable
								? `Creating new version based on v${currentVersion}`
								: `Viewing v${currentVersion}`}
						{versionStatus && versionStatus !== 'DRAFT' && (
							<span style={{ marginLeft: 8 }}><StatusBadge status={versionStatus} /></span>
						)}
					</div>
				</div>
				<div className="dsv-update__header-right">
					{isEditable && (
						<>
							<JsonUpload onUpload={handleJsonUpload} />
							<JsonDownload
								schema={finalSchema}
								dataElementId={dataElementId}
								version={currentVersion}
							/>
						</>
					)}
					{isEditable && hasFormChanges && (
						<button className="dsv-btn dsv-btn--primary" onClick={handleConfirmChanges}>
							Confirm Changes
						</button>
					)}
					{canEditSchema && transitionConfig && !isCreateMode && isVersionDataLoaded && (
						<div style={{ textAlign: 'right' }} data-testid="dsv-promote-region">
							<button
								className={`dsv-btn ${transitionConfig.buttonClass}`}
								onClick={showPromoteModal}
								disabled={!!disabledReason}
								title={disabledReason || enabledHint || undefined}
							>
								{transitionConfig.buttonLabel}
							</button>
							{disabledReason && (
								<p className="dsv-promote__hint" style={{ marginTop: 4, fontSize: 12 }}>
									{disabledReason}
								</p>
							)}
						</div>
					)}
					{isPageReady && <span data-testid="dsv-page-ready" style={{ display: 'none' }} />}
				</div>
			</div>

			{/* Version Info Bar — hidden in pure create mode (version is always v1.0.0 INITIAL) */}
			{!isPureCreateMode && (
				<div className="dsv-update__version-bar">
					<div className="dsv-update__version-field">
						<label className="dsv-update__version-label">Current Version</label>
						<div className="dsv-update__version-value">
							v{currentVersion}
						</div>
					</div>
					{isEditable && (
						<>
							<div className="dsv-update__version-field">
								<label className="dsv-update__version-label">New Version</label>
								<div className="dsv-update__version-value" data-testid="dsv-new-version">
									v{newVersion}
								</div>
							</div>
							<div className="dsv-update__version-field">
								<label className="dsv-update__version-label">Change Type</label>
								<select className="dsv-modal__select" value={changeType}
									onChange={(e) => setChangeType(e.target.value)}>
									{CHANGE_TYPES.map((ct) => (
										<option key={ct.value} value={ct.value}>{ct.name}</option>
									))}
								</select>
							</div>
						</>
					)}
				</div>
			)}

			{isEditable && (
				<div className="dsv-update__description">
					<label className="dsv-update__version-label">
						{isPureCreateMode ? 'Description' : 'Change Description'}
					</label>
					<textarea className={`dsv-modal__textarea ${formErrors.changeDescription ? 'dsv-modal__textarea--error' : ''}`} value={changeDescription}
						onChange={(e) => { setChangeDescription(e.target.value); setFormErrors(prev => ({ ...prev, changeDescription: undefined })); }}
						placeholder={isPureCreateMode
							? 'Describe the purpose of this data element...'
							: 'Describe what changes are being made in this version...'} rows={3} />
					{formErrors.changeDescription && <span className="dsv-form-error">{formErrors.changeDescription}</span>}
				</div>
			)}

			<div className="dsv-update__classifications">
				<ClassificationField
					name="security"
					label="Security Classification"
					options={SECURITY_CLASSIFICATIONS}
					value={securityClassification}
					onChange={(e) => { isDirtyRef.current = true; setSecurityClassification(e.target.value); }}
					error={formErrors.securityClassification}
					isEditable={isEditable}
				/>
				<ClassificationField
					name="sensitivity"
					label="Sensitivity Classification"
					options={SENSITIVITY_CLASSIFICATIONS}
					value={sensitivityClassification}
					onChange={(e) => { isDirtyRef.current = true; setSensitivityClassification(e.target.value); }}
					error={formErrors.sensitivityClassification}
					isEditable={isEditable}
				/>
			</div>

			{/* Tabs */}
			<div className="dsv-update__tabs">
				{tabs.map((tab) => (
					<div key={tab.key}
						className={`dsv-update__tab ${selectedTab === tab.key ? 'dsv-update__tab--active' : ''}`}
						onClick={() => setSelectedTab(tab.key)}>
						{tab.label}
					</div>
				))}
			</div>

			{/* Tab Content */}
			{selectedTab === 'edit' && (
				<>
					{isEditable && (
						<>
							<div className="dsv-schema__info-bar">
								<span className="dsv-schema__info-icon">&#9432;</span>
								<span>You are editing schema fields. Click on any field to modify its properties, or add new fields using the button below.</span>
							</div>
							<div className="dsv-schema__add-field-row">
								<button className="dsv-btn dsv-btn--primary" onClick={handleAddField}>+ Add Field</button>
							</div>
							{/*
							 * Bug #5: "Allow extra root fields" toggle. Defaults OFF so
							 * builder-authored DEs emit `additionalProperties: false`
							 * (strict) matching the upload-authored posture. Turn ON to
							 * omit the keyword and let the JSON Schema default of `true`
							 * apply for a permissive root shape.
							 */}
							<label className="dsv-schema__root-option">
								<span className="dsv-schema__root-option-text">
									<span className="dsv-schema__root-option-label">
										Allow extra root fields
										<span
											className="dsv-schema__root-option-help"
											aria-describedby="dsv-allow-extra-root-fields-tooltip"
											tabIndex={0}
											onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
										>
											<span aria-hidden="true">&#9432;</span>
											<span
												id="dsv-allow-extra-root-fields-tooltip"
												className="dsv-schema__root-option-tooltip"
												role="tooltip"
											>
												<strong>What this toggle does</strong>
												<br />
												When <strong>OFF</strong> (default, strict), the schema rejects any payload containing keys not declared in <code>properties</code>.
												<br /><br />
												When <strong>ON</strong> (permissive), extra keys are silently allowed.
												<br /><br />
												<strong>Why the saved JSON may look “backwards”</strong>
												<br />
												OFF writes <code>"additionalProperties": false</code> into the schema — strict mode has to be authored explicitly.
												<br /><br />
												ON <em>removes</em> the <code>additionalProperties</code> key entirely. JSON Schema’s default is already permissive (<code>true</code>), so omitting the keyword IS the permissive state — no need to author it.
												<br /><br />
												Applies to the root level only — nested object fields and array items have their own setting.
											</span>
										</span>
									</span>
									<span className="dsv-schema__root-option-hint">
										{allowExtraRoot
											? 'Permissive — payloads may include keys not declared in properties.'
											: 'Strict — payloads with keys not declared in properties will be rejected.'}
									</span>
								</span>
								<span className="switch">
									<input
										type="checkbox"
										data-testid="dsv-allow-extra-root-fields-toggle"
										checked={allowExtraRoot}
										onChange={(e) => { isDirtyRef.current = true; setAllowExtraRoot(e.target.checked); }}
									/>
									<span className="slider round" />
								</span>
							</label>
						</>
					)}
					<SchemaFieldsTable
						fields={localFields}
						onEditField={handleEditField}
						onDeleteField={handleDeleteField}
						onAddChild={handleAddChildField}
						onMoveUp={handleMoveFieldUp}
						onMoveDown={handleMoveFieldDown}
						canEditSchema={isEditable}
						viewMode={!isEditable}
						fieldToRulesIndex={!isEditable ? buildFieldToRulesIndex(xAllOfRules) : undefined}
					/>

					{isEditable ? (
						<XAllOfBuilder
							fields={localFields}
							rules={xAllOfRules}
							onRulesChange={(next) => { isDirtyRef.current = true; setXAllOfRules(next); }}
							onValidationChange={setXAllOfErrors}
						/>
					) : (
						<XAllOfRulesView rules={xAllOfRules} />
					)}
				</>
			)}

			{/*
			 * CustomValidatorsPanel owns `optedIn` state — the set of validator
			 * sections the user opened via the picker in this editing session.
			 * Render the panel always (hidden via CSS when inactive) so tab
			 * switches don't unmount it and reset opted-in sections that carry
			 * empty seeds (which fail isPresent).
			 */}
			<div style={{ display: selectedTab === 'customValidators' ? 'block' : 'none' }}>
				<CustomValidatorsPanel
					value={customValidators}
					onChange={(next) => { isDirtyRef.current = true; setCustomValidators(next); }}
					disabled={!isEditable}
					flattenedFieldOptions={localFields
						.filter((f) => f.status !== 'Deleted')
						.map((f) => f.dataElementName)
						.filter(Boolean)}
					masterDataTableOptions={masterDataTableOptions}
				/>
			</div>

			{selectedTab === 'json' && (
				<JsonViewer data={buildPayloadTemplate(
					localFields,
					isCreateMode ? undefined
						: (selectedVersion?.elementSchemaJson || selectedVersion?.elementSchema),
				)} />
			)}

			{selectedTab === 'schema' && (
				<div data-testid="dsv-schema-preview">
					<JsonViewer data={finalSchema} />
				</div>
			)}

			{/*
			 * ValidationPreview owns the pasted payload / result via useState.
			 * Render it always (hidden via CSS when inactive) so switching tabs
			 * doesn't unmount the component and wipe the user's draft.
			 */}
			<div style={{ display: selectedTab === 'validate' ? 'block' : 'none' }}>
				<ValidationPreview schema={finalSchema} />
			</div>

			{selectedTab === 'changes' && (
				<VersionComparison
					versions={versionHistory || []}
					workingCopy={compareWorkingCopy}
					// Restore the previously-chosen Base/Target after a tab switch; fall
					// back to the defaults only until a real selection has been made (so an
					// open-before-data-loads race can't get stuck on an empty base).
					initialBase={(compareSelection && compareSelection.base) || selectedVersion?.version}
					initialTarget={(compareSelection && compareSelection.target) || '__working_copy__'}
					// `false` is a valid value, so pick it conditionally rather than with `||`.
					initialOnlyChanges={compareSelection ? compareSelection.onlyChanges : undefined}
					onSelectionChange={setCompareSelection}
					compact
				/>
			)}

			{selectedTab === 'impact' && (
				<div className="dsv-update__impact">
					<div className="dsv-update__impact-summary">
						<h3>Impact Analysis</h3>
						<p>
							This version change will affect{' '}
							<strong>{impactAnalysis?.subscribedOrganizations || 0}</strong> organisations
							currently using this data element.
						</p>
						<AffectedOrganisations organizations={impactAnalysis?.organizations} />
						{impactAnalysis && (
							<>
								<p>Risk Score: <strong>{impactAnalysis.riskScore}</strong></p>
								<p>Integrations Count: <strong>{impactAnalysis.integrationsCount}</strong></p>
								<p>Estimated Migration Time: <strong>{impactAnalysis.estimatedMigrationTime}</strong></p>
							</>
						)}
					</div>
				</div>
			)}

			{/* Field Modal */}
			<FieldModal
			visible={visibleFieldModal}
			hide={() => { hideFieldModal(); setEditingParentFieldId(null); }}
			field={editingField}
			onSave={handleSaveField}
			parentFieldId={editingParentFieldId}
			currentDepth={editingParentFieldId ? getFieldDepth(editingParentFieldId) + 1 : 0}
		/>

			{/* Confirm Version Modal */}
			<ConfirmVersionModal
				visible={visibleConfirmModal}
				hide={hideConfirmModal}
				versionInfo={isPureCreateMode
				? { elementName: newDataElementName, currentVersion: '—', newVersion: '1.0.0', changeType: 'INITIAL', changeDescription }
				: { elementName, currentVersion, newVersion, changeType, changeDescription }}
				schemaDiff={diffAgainstBase(localFields, baseSchemaForDiff)}
				payloadPreview={buildPayloadTemplate(localFields, baseSchemaForDiff)}
				schemaPreview={finalSchema}
				ruleDiff={ruleDiff}
				rootMetaDiff={rootMetaDiff}
				customValidatorsDiff={customValidatorsDiff}
				fieldOrderDiff={fieldOrderDiff}
				onConfirm={handleSubmitVersion}
			/>

			{/* Promote Modal */}
			<PromoteModal
				visible={visiblePromoteModal}
				hide={hidePromoteModal}
				version={selectedVersion}
				onConfirm={handleConfirmPromote}
			/>

			{/* Schema Validation Errors */}
			{schemaValidationErrors.length > 0 && (
				<div className="dsv-schema__validation-errors" data-testid="schema-validation-errors" style={{ margin: '12px 0', padding: 12, border: '1px solid #fca5a5', borderRadius: 6, backgroundColor: '#fef2f2' }}>
					<p style={{ fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>Schema Validation Errors:</p>
					<ul style={{ margin: 0, paddingLeft: 20 }}>
						{schemaValidationErrors.map((err, i) => (
							<li key={i} style={{ color: '#dc2626', fontSize: 13 }}>{err}</li>
						))}
					</ul>
				</div>
			)}

			{/* Conflict Modal (409) */}
			<DSModal visible={visibleConflictModal} close={hideConflictModal} variant="small" dismissClickOutsideModal={false}>
				<DSCardConfigure style={{ minWidth: 400 }}>
					<DSCardConfigure.Header>
						<DSCardConfigure.Header.Title>Conflict Detected</DSCardConfigure.Header.Title>
						<DSCardConfigure.Header.Close>
							<button className="dsv-icon-btn" onClick={hideConflictModal} title="Close">&times;</button>
						</DSCardConfigure.Header.Close>
					</DSCardConfigure.Header>
					<DSCardConfigure.Body>
						<DSCardConfigure.Body.Col>
							<p data-testid="conflict-message">This draft has been modified by another user. Please reload to get the latest version.</p>
						</DSCardConfigure.Body.Col>
					</DSCardConfigure.Body>
					<DSCardConfigure.Footer>
						<DSCardConfigure.Footer.Left />
						<DSCardConfigure.Footer.Right>
							<button className="dsv-btn dsv-btn--ghost" onClick={hideConflictModal}>Cancel</button>
							<button className="dsv-btn dsv-btn--primary" style={{ marginLeft: rem(12) }} onClick={handleConflictReload} data-testid="conflict-reload-btn">
								Reload
							</button>
						</DSCardConfigure.Footer.Right>
					</DSCardConfigure.Footer>
				</DSCardConfigure>
			</DSModal>

			{/* Loading overlay */}
			{loading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/img/loading.gif")} alt="loading" />
						</div>
					</div>,
					document.body
				)}

			<Toast toastList={toastList} />
		</>
	);
};

export default DSVUpdateSchemaPage;
